// Grids & Gantts — THE STRUCTURE. Every grid on every site renders this one
// component over its own row; a fix here reaches all of them at once.
//
// A grid is a Jira-style structure: a tree assembled from GENERATORS (insert
// queries, extenders by the Parent field or a link type, groupings, sorts,
// a filter every level passes through), drawn as a windowed tree grid with
// an optional timeline pane, editable in place, exportable, and — in admin
// mode — built and changed right here through Page settings.
//
// Nothing about any client is in this file. The folders, their insert
// queries, their extenders, their groupings, their sorts, the columns and
// the filters are all DATA on the grid's row (page.config[configKey]),
// edited in Stride admin mode. The product's defaults are the EMPTY
// structure (see ./config.js); a host may pass its own.
//
// HOW IT LOADS, and why it is a config knob rather than a decision in code.
// Two loaders, one engine; `structure.js` cannot tell them apart, and its
// suite proves the two build the same tree.
//
//   walk (the default)   a query per level, `walkChunk` parents at a time,
//                        loading ONLY what the structure reaches.
//   universe             one broad paged load of everything the filter
//                        admits, assembled in memory.
//
// Which one is right is a fact about the SITE, not about the page: on a site
// where the structure reaches a third of the work, walking it loads a third
// of what the broad load would; where it covers most of the work, the
// arithmetic reverses. The admin's Data scope note prints the numbers.
//
// Props: { page, api, saveConfig, configKey, defaults }.
//   page        the grid's row: { slug, name, config }
//   api         the REST facade
//   saveConfig  async (patch, page) => void — persists a patch of config keys
//               ({ [configKey], [configKey + 'History'] }) over the row
//   configKey   which key on the row holds this structure's config
//   defaults    what an unset field means — EMPTY_STRUCTURE unless told

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { useLookAndFeel } from '@platform/app-shell/frontend/lookAndFeel';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { isAdminUser } from '@platform/app-shell/frontend/utils/adminAuth';
import { socket } from '@platform/http/socket';
import { DARK, FONT, LIGHT } from './theme';
import MultiPick from './MultiPick';
import { toExcel, toPdf } from './exports';
import { resolveField, fieldLabel } from './fields';
import TypeIcon, { useWorkTypes } from '../shared/TypeIcon';
import WorkItemPopup from '../shared/WorkItemPopup';
import WorkItemPage from '../shared/WorkItemPage';
import { usePhone } from '../shared/phone';
import {
  allPaths, chunkKeys, excludedZqlFor, expandFolder, FILTER_CHUNK, FILTER_PARALLEL, flattenStructure, linkedChildKeys, linkedParentKeys, makeCounter,
  makeLoadCache, makePool, memorySource, pruneToMatches, keepRoots, buttonTests, querySource, withoutItem, movedItem, FIELD_ID_SORT,
} from './structure';
import { cellOf, isDone, resolvePseudo, shownColumns, statusOf, textsOf, typeOf, typeTest } from './columns';
import { lineHas, linesIndex, tablesIn } from './lines';
import { netStock } from './stock';
import { figureOf } from './lookups';
import { DONE_MODES, buttonParts, pressButton, cloneSettingsOf, descendantFolderIds, fieldNamesOf, formulaNamesOf, frozenIdsOf, isCloneable, mayClone, normalizeConfig, removeFolderQuestion, rescheduleOf, sortFieldOf, withoutRule } from './config';
import { mayReschedule, rescheduleRuleFor, shiftDates, typeNameOf as rescheduleTypeOf, wholeMove } from './reschedule';
import { RescheduleChip, RescheduleReport } from './Rescheduler';
import { compileFormula, dateBucket, describeGroup, formulaGroup, groupFieldIds, groupIsSet, groupZqls, orderIn, presetGroup, statusRank, zqlGroup } from './grouping';
import { windowOf } from './timeline';
import { pushLater } from './schedule';
import { buildDependencyEdges, buildLinkPayload } from '@modules/folders/frontend/utils/ganttDependencyHelpers';
import { inflate, patchForGrid, unchangedForGrid } from './compact';
import { readStore } from '../shared/userView';
import { runClone } from './clone';
import ClonePanel from './ClonePanel';
import { DnaIcon } from '../shared/CloneIcon';
import ZqlBox from './ZqlBox';
import { prefetchEditMeta } from './EditField';
import { createChild, loadTypes, preflightCreate, searchParents, validatorRefusal, warmProjects } from './hierarchy';
import { CreateScreen } from './RowControls';
import Grid from './Grid';
import { ROW_H, ROW_H_TOUCH } from './icons';
import RuleEditor from './RuleEditor';
import useLookups from './useLookups';
import SettingsPanel from './SettingsPanel';

const MAX_CAP = 200000;
// A grid's queries were written for Jira: `~` matches whole words, as Jira
// reads it (see wordsMatch in the ZQL compiler) — the server build, the quick
// buttons, the ZQL box and the Test button alike.
const WORDS = { textMode: 'words' };
// The work item keys a field value names: a parent { key }, an Epic Link's
// key, a list of either.
const KEY_RE = /^[A-Z][A-Z0-9_]*-\d+$/;
const refKeysIn = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) return [...new Set(v.flatMap(refKeysIn))];
  if (typeof v === 'object') return v.key && KEY_RE.test(String(v.key)) ? [String(v.key)] : [];
  const s = String(v).trim().toUpperCase();
  return KEY_RE.test(s) ? [s] : [];
};
const NO_LINES = [];
// The headings of the first table found in a field over the loaded work items
// (of these types) — what the table-lines editor offers as column names.
function tableHeadings(items, fieldId, types) {
  if (!fieldId) return [];
  const want = (types || []).map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  for (const it of items.values()) {
    const t = String((it.fields && it.fields.issuetype && it.fields.issuetype.name) || '').toLowerCase();
    if (want.length && !want.includes(t)) continue;
    const tables = tablesIn(it.fields && it.fields[fieldId]);
    if (tables.length && tables[0].headings.length) return tables[0].headings.filter(Boolean);
  }
  return [];
}
const EMPTY_SET = new Set();
// Every value this page has loaded for a work item, matched as text. This
// is the browser's half of text mode: it reaches any field the columns,
// filters or groupings caused to load, which is more than ZQL's free-text
// targets but less than "everything" — a field nobody asked for was never
// fetched, and no amount of client-side cleverness can search it.
function matchesLoadedText(it, needle) {
  const f = (it && it.fields) || {};
  for (const k of Object.keys(f)) {
    const v = f[k];
    if (v == null || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number') {
      if (String(v).toLowerCase().includes(needle)) return true;
    } else if (Array.isArray(v)) {
      for (const x of v) {
        const t = x && typeof x === 'object' ? (x.displayName || x.value || x.name || x.key || '') : x;
        if (t && String(t).toLowerCase().includes(needle)) return true;
      }
    } else if (typeof v === 'object') {
      const t = v.displayName || v.value || v.name || v.key || '';
      if (t && String(t).toLowerCase().includes(needle)) return true;
    }
  }
  return String((it && it.key) || '').toLowerCase().includes(needle);
}

const SORT_NAMES = {
  duedate: 'Due date', summary: 'Summary', key: 'Key', status: 'Status',
  type: 'Work type', start: 'Start date', none: 'Insertion order',
};
const lower = (s) => String(s || '').toLowerCase();
const andZql = (a, b) => (a && a.trim() && b && b.trim() ? `(${a.trim()}) AND (${b.trim()})` : (a && a.trim() ? a.trim() : (b || '').trim()));

// THIS COMPONENT IS THE STRUCTURE, and there is exactly one of it.
//
// A second grid is not a copy of this file — it is another row, rendered by
// the same component. A host that keeps its own structure pages (a client
// fork's custom pages, say) renders it with its own `configKey`, its own
// `defaults` and its own `saveConfig`; the product's grids host passes the
// empty structure and the grids API.
//
//   configKey   which key on the row holds this structure's config, so two
//               structures on one row never read each other's rules
//   defaults    what an unset field means — EMPTY_STRUCTURE unless told
//   saveConfig  where a saved structure goes — the host's own API
//   toolbarStart  the host's own button(s) at the start of the toolbar,
//               before the grid's name (optional)
//   fitRows     the grid only as tall as its open rows (up to the height it
//               would have) — for a page that draws it inside its own content
export default function Structure({ page, api, saveConfig, configKey = 'structure', defaults = undefined, minGridHeight = 0, toolbarStart = null, fitRows = false }) {
  const HIST = `${configKey}History`;
  // Where this reader's own view of the grid is kept — the same key the grid
  // reads, because the page has to know which fields THEIR columns want.
  const storeKey = `${configKey}-${(page && page.slug) || 'main'}`;
  const { adminMode, openTab } = useTab();
  // The reader's groups: who may clone, who may reschedule.
  const { userGroups } = useAuth();
  // ?item=KEY — one work item, its own screen, and NO STRUCTURE LOAD. Read
  // before anything else because the load effect is gated on it.
  const soloKey = useMemo(() => {
    if (typeof window === 'undefined') return '';
    try { return new URLSearchParams(window.location.search).get('item') || ''; } catch { return ''; }
  }, []);
  const lookAndFeel = useLookAndFeel();
  const workTypes = useWorkTypes(api);

  // ---- settings ---------------------------------------------------------------
  const [savedCfg, setSavedCfg] = useState(() => normalizeConfig(page && page.config && page.config[configKey], defaults));
  const [draft, setDraft] = useState(() => normalizeConfig(page && page.config && page.config[configKey], defaults));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedFlag, setSavedFlag] = useState(false);
  const [history, setHistory] = useState(() => (Array.isArray(page && page.config && page.config[HIST]) ? page.config[HIST].slice(0, 3) : []));
  const cfg = useMemo(() => normalizeConfig(draft, defaults), [draft, defaults]);
  const dirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(savedCfg), [cfg, savedCfg]);
  const pageRef = useRef(page); useEffect(() => { pageRef.current = page; }, [page]);

  const saveSettings = async (override, { keepHistory = true } = {}) => {
    const toSave = override ? normalizeConfig(override, defaults) : cfg;
    setSaving(true); setSaveErr(null);
    try {
      const nextHistory = keepHistory ? [{ at: new Date().toISOString(), cfg: savedCfg }, ...history].slice(0, 3) : history;
      const p = pageRef.current;
      // A PATCH, never the whole row: the structure and its rollback history
      // are all this page may write. The name, the description and who may
      // open the grid belong to the Settings card and are not in reach here.
      const patch = { [configKey]: toSave, [HIST]: nextHistory };
      if (typeof saveConfig !== 'function') throw new Error('This structure has nowhere to save to: the host page passed no saveConfig');
      await saveConfig(patch, p);
      pageRef.current = { ...p, config: { ...(p.config || {}), ...patch } };
      if (override) setDraft(toSave);
      setSavedCfg(toSave); setSavedFlag(true); setHistory(nextHistory);
    } catch (e) {
      setSaveErr((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Save failed');
    } finally { setSaving(false); }
  };
  const revertSettings = () => { setDraft(savedCfg); setSaveErr(null); };
  // THE OTHER GRIDS ON THIS SITE, and one grid's cloning settings — what
  // Page settings → Cloning copies from. A host without the grids API (or
  // this grid itself) simply has nothing to offer.
  const listOtherGrids = useCallback(async () => {
    if (!api || typeof api.gridsList !== 'function') return [];
    const d = await api.gridsList();
    const mine = page && page.slug;
    return ((d && d.grids) || []).filter((g) => g && g.slug && g.slug !== mine);
  }, [api, page]);
  const cloningOfGrid = useCallback(async (slug) => {
    const g = await api.gridsGet(slug);
    return cloneSettingsOf(g && g.config && g.config.structure);
  }, [api]);
  const rescheduleOfGrid = useCallback(async (slug) => {
    const g = await api.gridsGet(slug);
    return rescheduleOf(g && g.config && g.config.structure);
  }, [api]);
  const rollBack = (h) => { setDraft(normalizeConfig(h.cfg, defaults)); setSaveErr(null); };
  const testZql = async (zql) => {
    const r = await api.countIssues(zql, null, WORDS);
    return typeof r === 'object' && r ? (r.count != null ? r.count : r.total || 0) : (r || 0);
  };

  // ---- theme, viewport --------------------------------------------------------
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.getAttribute('data-theme') === 'dark');
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  // A PHONE, DETECTED THE WAY THE FORK'S OTHER PAGES DETECT ONE.
  //
  // A media query on its own missed the case that matters: the app shell
  // offers a "desktop site" switch, and a phone using it reports a 1280px
  // viewport while still being a phone in every way — no hover, a coarse
  // pointer, four inches of glass. `usePhone` also takes the viewport tag
  // back while this page is open, so a phone layout arrives at device width
  // instead of as a shrunken photograph of a desktop one. See ../shared/phone.
  const narrow = usePhone();
  const ui = useMemo(() => ({ ...(isDark ? DARK : LIGHT), radius: 12, radiusSm: 8, radiusFull: 999 }), [isDark]);
  const S = useMemo(() => ({
    scroll: { height: '100%', overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable', background: ui.page, color: ui.fg, fontFamily: FONT },
    wrap: { width: '100%', boxSizing: 'border-box', padding: narrow ? '8px 10px 24px' : '8px 18px 24px' },
    h1: { fontSize: narrow ? 18 : 20, margin: 0, fontWeight: 700, lineHeight: 1.2, letterSpacing: -0.2 },
    label: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', margin: '0 0 6px', color: ui.muted, textTransform: 'uppercase' },
    tiny: { fontSize: 12, color: ui.muted },
    input: { width: '100%', padding: '9px 12px', borderRadius: ui.radiusSm, border: `1px solid ${ui.border}`, background: ui.card, color: ui.fg, fontSize: 13.5, boxSizing: 'border-box', outline: 'none', fontFamily: FONT, colorScheme: isDark ? 'dark' : 'light' },
    card: { border: `1px solid ${ui.border}`, borderRadius: ui.radius, background: ui.card, boxShadow: ui.shadowSm, padding: 16 },
    pill: (on) => ({ fontSize: 12.5, fontWeight: 700, padding: '5px 12px', borderRadius: ui.radiusFull, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${on ? ui.primary : ui.border}`, background: on ? `${ui.primary}14` : ui.card, color: on ? ui.fg : ui.muted, fontFamily: FONT }),
    exportBtn: { fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${ui.border}`, background: ui.card, color: ui.muted, fontFamily: FONT },
  }), [ui, isDark, narrow]);

  // ---- field wiring -----------------------------------------------------------
  const [fieldList, setFieldList] = useState(null);
  useEffect(() => {
    let on = true;
    api.getFields().then((f) => { if (on) setFieldList(Array.isArray(f) ? f : []); }).catch(() => { if (on) setFieldList([]); });
    return () => { on = false; };
  }, [api]);
  const fieldsReady = fieldList !== null;

  // The timeline toggle is declared HERE, above the column set, because
  // the column set reads it — a reader can switch the timeline on and the
  // columns beside it change with it.
  //
  // EVERY GRID OPENS WITH THE CHART SHUT. The grid's "Offer the Gantt chart"
  // (config gantt.on) says whether the 📊 button is there to open it at all;
  // opening it is the reader's click, every time — a grid that came up with
  // the chart taking half the screen was not what anybody had asked to see.
  const [ganttOn, setGanttOn] = useState(false);
  // The grain lives HERE rather than only inside the grid, because the PDF
  // export needs it too — a chart printed at a different scale from the one
  // on screen is a different chart. The grid still owns the buttons and the
  // remembering; this is just where the answer is kept.
  const [ganttGrain, setGanttGrain] = useState(() => {
    try { return localStorage.getItem(`grids.${configKey}-${(page && page.slug) || 'main'}.grain`) || ''; } catch { return ''; }
  });
  // A grid whose chart is no longer offered (an admin unticking it in Page
  // settings) shuts it; ticking it again only brings the button back.
  useEffect(() => { if (!cfg.gantt.on) setGanttOn(false); }, [cfg.gantt.on]);

  // With the timeline on, the columns share the width with it. A structure
  // with thirteen of them leaves the chart nothing, so the config names the
  // few worth keeping beside a bar — and an empty list means all of them.
  const allCols = useMemo(() => shownColumns(cfg), [cfg]);
  const cols = useMemo(() => {
    const keep = (cfg.gantt.columns || []);
    if (!ganttOn || !keep.length) return allCols;
    const picked = allCols.filter((c) => keep.includes(c.id));
    // Never leave the tree column out — without it there are no twisties
    // and no structure, only a list.
    if (picked.some((c) => c.kind === 'summary')) return picked;
    const tree = allCols.find((c) => c.kind === 'summary');
    return tree ? [tree, ...picked] : picked;
  }, [allCols, ganttOn, cfg.gantt.columns]);
  // Every column's field, resolved against THIS site, keyed by column id —
  // a rename in Jira changes the id, not the config.
  const fieldIds = useMemo(() => {
    const o = {};
    (cfg.columns || []).forEach((c) => { o[c.id] = c.field === 'duedate' ? 'duedate' : (resolvePseudo(c.field) || resolveField(c.field, fieldList || [])); });
    return o;
  }, [cfg.columns, fieldList]);
  const filterDefs = useMemo(() => cfg.filters.map((f) => ({
    ...f, fieldId: resolvePseudo(f.field) || resolveField(f.field, fieldList || []), label: f.label || fieldLabel(f.field, fieldList || []) || f.field,
  })), [cfg.filters, fieldList]);
  const groupIds = useMemo(() => {
    const o = {};
    (cfg.folders || []).forEach((f) => (f.groups || []).forEach((g) => {
      o[`${f.id}:${g.id}`] = resolvePseudo(g.field) || resolveField(g.field, fieldList || []);
    }));
    return o;
  }, [cfg.folders, fieldList]);
  // WHAT THE FORMULAS AND THE FIELD SORTS READ, by this site's id. A field
  // nothing else asks for would never be loaded, and the formula or the sort
  // would see a blank on every row.
  const extraFieldIds = useMemo(() => {
    const names = new Set(formulaNamesOf(cfg));
    [cfg.sortRoot, ...(cfg.folders || []).flatMap((f) => [f.sort, ...(f.sorts || []).map((s) => s.by)])].forEach((x) => { const n = sortFieldOf(x); if (n) names.add(n); });
    // a folder's TABLE LINES: the field its table is in, and the fields its lines fill
    (cfg.folders || []).forEach((f) => {
      const l = f.expand && f.expand.lines;
      if (!l) return;
      names.add(l.field);
      (l.map || []).forEach((m) => names.add(m.field));
    });
    return [...names].map((n) => (n === 'duedate' ? 'duedate' : (resolvePseudo(n) || resolveField(n, fieldList || [])))).filter(Boolean);
  }, [cfg, fieldList]);
  // A sort as the ENGINE reads it: `field:<name>` becomes `fieldid:<id>`
  // here, once the site's fields are known; every other sort is itself.
  const engineSort = useCallback((x) => {
    const n = sortFieldOf(x);
    if (!n) return x;
    const id = n === 'duedate' ? 'duedate' : (resolvePseudo(n) || resolveField(n, fieldList || []));
    return id ? `${FIELD_ID_SORT}${id}` : 'none';
  }, [fieldList]);
  const sortRootEff = useMemo(() => engineSort(cfg.sortRoot), [engineSort, cfg.sortRoot]);
  const missingFields = useMemo(() => fieldNamesOf(cfg)
    .filter((n) => n !== 'duedate' && !resolvePseudo(n) && !resolveField(n, fieldList || [])), [cfg, fieldList]);

  // THE READER'S OWN COLUMNS, as they were when the page opened. A column a
  // reader added names a field the config never asked for, so it was never
  // loaded and the column was blank on every row. Their view is read once
  // here so those fields ride along on the load; a column added AFTER the
  // load is filled by `onFieldsWanted` below.
  const mineFieldIds = useMemo(() => {
    const added = (readStore(storeKey).added || []);
    return added.map((c) => (c.field === 'duedate' ? 'duedate' : (resolvePseudo(c.field) || resolveField(c.field, fieldList || [])))).filter(Boolean);
  }, [storeKey, fieldList]);

  // `issuelinks` and `parent` are not decoration — they are how a row finds
  // what hangs under it, so they load on every level.
  const NOT_FIELDS = ['project', 'key', 'projectCategory', 'projectName'];
  const LOAD_FIELDS = useMemo(() => {
    const ids = new Set(['summary', 'issuetype', 'status', 'duedate', 'project', 'parent', 'issuelinks']);
    Object.values(fieldIds).forEach((id) => { if (id && !NOT_FIELDS.includes(id)) ids.add(id); });
    Object.values(groupIds).forEach((id) => { if (id && !NOT_FIELDS.includes(id)) ids.add(id); });
    // A preset grouping may read a field the columns never asked for.
    groupFieldIds(cfg).forEach((id) => { if (id && !NOT_FIELDS.includes(id)) ids.add(id); });
    filterDefs.forEach((f) => { if (f.fieldId && !NOT_FIELDS.includes(f.fieldId)) ids.add(f.fieldId); });
    mineFieldIds.forEach((id) => { if (!NOT_FIELDS.includes(id)) ids.add(id); });
    extraFieldIds.forEach((id) => { if (!NOT_FIELDS.includes(id)) ids.add(id); });
    return [...ids].join(',');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldIds, groupIds, filterDefs, mineFieldIds, extraFieldIds, cfg.folders]);

  // THE WORK ITEMS THE ROWS NAME (see refs in ../../server/build.js). The
  // fields whose values ARE work items — the Parent, an Epic Link — and what
  // the page asks the server to read beside the structure: the parent of
  // every row when a Parent / Epic column shows it by name, the work item a
  // grouping by such a field draws as its header, and the work items a link
  // grouping hangs rows under.
  const refFieldIds = useMemo(() => {
    const s = new Set(['parent']);
    (fieldList || []).forEach((fl) => {
      const custom = String((fl && fl.schema && fl.schema.custom) || '');
      if (/gh-epic-link|jpo-custom-field-parent/.test(custom)) s.add(fl.id);
    });
    return s;
  }, [fieldList]);
  const refSpec = useMemo(() => {
    const ids = new Set();
    if ((cfg.columns || []).some((c) => c.kind === 'parent')) ids.add('parent');
    const links = [];
    (cfg.folders || []).forEach((fo) => (fo.groups || []).forEach((g) => {
      if (g.kind === 'link' && g.linkRelation) links.push({ type: g.linkType || '', relation: g.linkRelation });
      if (g.kind === 'field' && g.field) {
        const id = resolvePseudo(g.field) || resolveField(g.field, fieldList || []);
        if (id && refFieldIds.has(id)) ids.add(id);
      }
    }));
    return { fields: [...ids], links };
  }, [cfg.columns, cfg.folders, fieldList, refFieldIds]);

  // ---- the load ---------------------------------------------------------------
  const [built, setBuilt] = useState(null);   // [{ folder, roots, byKey, kidsOf }]
  // The work items the rows name but no folder holds (refSpec), by key.
  const [refItems, setRefItems] = useState(() => new Map());
  // Counts fresh loads — what the open/shut reset keys on. See the load.
  const [loadGen, setLoadGen] = useState(0);
  const [progress, setProgress] = useState({ rows: 0, phase: '' });
  const [error, setError] = useState(null);
  const [loadInfo, setLoadInfo] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  // A QUIET RELOAD keeps the tree on screen while the new one is built, and
  // swaps it in when it is ready — what a work item created elsewhere that
  // belongs at the top of a folder (a new Project) asks for, so it appears
  // without anybody pressing ↻. `quietNext` says the next load is one;
  // `quietBusy` is true while it runs; work items created meanwhile are
  // kept in `createdDuringQuiet` and hung on the new tree once it lands.
  const quietNext = useRef(false);
  const quietBusy = useRef(false);
  const quietTimer = useRef(null);
  const createdDuringQuiet = useRef(new Map());

  const fetchAll = useCallback(async (jql, fields, onPage, max) => {
    const out = []; let startAt = 0;
    const size = cfg.pageSize;
    for (;;) {
      /* eslint-disable no-await-in-loop */
      const res = await api.searchIssues(jql, fields, size, null, startAt, true, WORDS);
      /* eslint-enable no-await-in-loop */
      const issues = (res && res.issues) || (res && res.data && res.data.issues) || (Array.isArray(res) ? res : []) || [];
      out.push(...issues);
      if (onPage) onPage(issues.length, out.length);
      if (issues.length < size || out.length >= (max || MAX_CAP)) break;
      startAt += issues.length;
    }
    return out;
  }, [api, cfg.pageSize]);

  const loadKey = JSON.stringify({
    f: cfg.filterZql, fm: cfg.filterMode, mx: cfg.maxExcluded, m: cfg.loadMode, u: cfg.universeZql, r: cfg.maxRows, p: cfg.pageSize, w: cfg.walkChunk, wp: cfg.walkParallel,
    folders: cfg.folders.map((x) => ({ i: (x.inserts || []).map((q) => q.zql), f: x.filterZql, e: x.expand, in: x.include, ex: x.exclude })),
    L: LOAD_FIELDS,
    R: refSpec,
  });
  useEffect(() => {
    // Solo mode asked for ONE work item. Walking the structure to show it
    // would be a minute of somebody's life for nothing.
    if (!fieldsReady || soloKey) return undefined;
    let on = true;
    const quiet = quietNext.current && !!built;
    quietNext.current = false;
    quietBusy.current = quiet;
    if (!quiet) { setBuilt(null); setError(null); setProgress({ rows: 0, phase: 'starting' }); }
    // A fresh load carries only LOAD_FIELDS; whatever was filled in on top
    // of the last one is gone with it.
    fetchedExtra.current = new Set();
    (async () => {
      const startedAt = Date.now();
      try {
        // ON THE SERVER, IN ONE REQUEST. The same walk, over the same engine,
        // against the API's own database; the answer streams progress and
        // then the compact shape (./compact.js), inflated here into the
        // parts the rest of the page reads exactly as before.
        if (cfg.loadMode === 'server') {
          if (on) setProgress({ rows: 0, phase: 'building on the server' });
          const result = await api.gridsBuild({
            structure: {
              filterZql: cfg.filterZql, filterMode: cfg.filterMode, maxExcluded: cfg.maxExcluded, maxRows: cfg.maxRows,
              walkChunk: cfg.walkChunk, walkParallel: cfg.walkParallel,
              folders: cfg.folders.map((f) => ({ id: f.id, name: f.name, inserts: f.inserts, filterZql: f.filterZql, expand: f.expand, include: f.include, exclude: f.exclude })),
            },
            fields: LOAD_FIELDS.split(','),
            refs: refSpec,
          }, { onProgress: (p) => { if (on) setProgress({ rows: p.rows || 0, phase: p.phase || 'building on the server' }); } });
          if (!on) return;
          const got = inflate(result);
          const byFolder = new Map(got.parts.map((p) => [p.folderId, p]));
          const parts = cfg.folders.map((folder) => {
            const p = byFolder.get(folder.id);
            return p ? { folder, roots: p.roots, byKey: p.byKey, kidsOf: p.kidsOf, depth: p.depth }
              : { folder, roots: [], byKey: new Map(), kidsOf: new Map(), depth: 0 };
          });
          quietBusy.current = false;
          setBuilt(parts);
          setRefItems(got.refs || new Map());
          setLoadGen((g) => g + 1);
          const st = result.stats || {};
          setLoadInfo({
            seconds: (Date.now() - startedAt) / 1000,
            serverSeconds: st.seconds,
            excluded: st.excluded == null ? null : st.excluded,
            filterNote: st.filterNote || '',
            universe: 0,
            capped: !!st.capped,
            folders: st.folders || parts.map((p) => ({ name: p.folder.name, roots: p.roots.length, items: p.byKey.size, depth: p.depth })),
            queries: st.queries,
            saved: st.cacheSaved,
            requests: st.requests,
            wireBytes: result.wireBytes,
            error: null,
          });
          setProgress({ rows: 0, phase: '' });
          // A SERVER THAT PREDATES THE REFS (its stats carry no count of them)
          // sends none: the page reads the work items its rows name itself, the
          // same set the server would have read, once the rows are drawn — the
          // headers and the Parent cells fill in when they arrive.
          if (st.refs === undefined && (refSpec.fields.length || refSpec.links.length)) {
            const held = new Set();
            parts.forEach((p) => p.byKey.forEach((_, k) => held.add(k)));
            const want = new Set();
            parts.forEach((p) => p.byKey.forEach((it) => {
              refSpec.fields.forEach((id) => refKeysIn(it.fields && it.fields[id]).forEach((k) => want.add(k)));
              refSpec.links.forEach((rule) => linkedParentKeys(it, rule).forEach((k) => want.add(k)));
            }));
            const keys = [...want].filter((k) => !held.has(k));
            // A failure here leaves the headers as keys; it never fails the grid.
            if (keys.length) {
              try {
                const pool = makePool(FILTER_PARALLEL);
                const rows = await Promise.all(chunkKeys(keys, FILTER_CHUNK).map((chunk) => pool(() => fetchAll(
                  `key in (${chunk.join(', ')})`, LOAD_FIELDS, null, MAX_CAP,
                ))));
                if (!on) return;
                const m = new Map();
                rows.forEach((list) => (list || []).forEach((r) => { if (r && r.key) m.set(r.key, r); }));
                setRefItems(m);
              } catch { /* the headers stay as keys */ }
            }
          }
          return;
        }

        const structFilter = cfg.filterZql.trim();
        let source = null;
        let universeCount = 0;
        let capped = false;

        // KEEP-ANCESTORS: the filter says what is SHOWN, not what is
        // reached, so the walk must not fold it into its queries. What it
        // needs instead is the set of work items the filter REJECTS — asked
        // for as the filter's complement, which is normally a rounding error
        // next to the site (305 rows out of 90 000 here). Anything that
        // turns out not to be gets the cheap reading instead, loudly.
        let excluded = null;        // Set of keys the filter rejects
        let filterNote = '';
        const wantAncestors = cfg.filterMode === 'keep-ancestors' && !!structFilter && cfg.loadMode === 'walk';
        if (wantAncestors) {
          const notZql = excludedZqlFor(structFilter);
          try {
            if (on) setProgress({ rows: 0, phase: 'finding what the filter leaves out' });
            const rows = await fetchAll(`${notZql} order by key asc`, 'key', null, cfg.maxExcluded + 1);
            if (rows.length > cfg.maxExcluded) {
              filterNote = `the filter leaves out more than ${cfg.maxExcluded.toLocaleString()} work items, so the structure was built the cheap way (a rejected row takes its branch with it)`;
            } else {
              excluded = new Set(rows.map((r) => r.key));
            }
          } catch (e) {
            filterNote = `the filter's complement could not be read (${(e && e.message) || 'failed'}), so the structure was built the cheap way`;
          }
        }
        // What the per-level queries carry. With keep-ancestors in force the
        // answer is NOTHING — the pruning happens after the walk.
        const queryFilter = excluded ? '' : structFilter;

        if (cfg.loadMode === 'universe') {
          const uq = (cfg.universeZql.trim() || structFilter);
          if (!uq) throw new Error('This structure has no filter and no broad-load query, so a single load would be the whole site. Give it one, or switch the load to level by level.');
          if (on) setProgress({ rows: 0, phase: 'loading the structure' });
          const universe = await fetchAll(`${uq} order by key asc`, LOAD_FIELDS,
            (n, total) => { if (on) setProgress({ rows: total, phase: 'loading the structure' }); }, cfg.maxRows);
          if (!on) return;
          universeCount = universe.length;
          capped = universe.length >= cfg.maxRows;
          source = memorySource(universe);
        }

        // THE FOLDERS DO NOT WAIT FOR EACH OTHER. They are independent —
        // each has its own insert queries and its own walk — and running them
        // one after another meant the last one started a minute in.
        //
        // What they DO share is one pool and one cache, so this is not three
        // times the load on the server: `walkParallel` still caps how many
        // queries this page has in flight AT ALL, and the sharing keeps those
        // slots full instead of idling at the end of every folder's level.
        // The cache is the bigger half — these folders all insert Projects
        // and then all walk those Projects' children, and the second and
        // third used to ask the server for rows already in memory.
        const pool = makePool(cfg.walkParallel);
        const loadCache = makeLoadCache();
        const progressOf = new Map();   // folder id -> items reached so far
        const bumpRows = () => {
          if (!on || cfg.loadMode !== 'walk') return;
          let total = 0;
          progressOf.forEach((n) => { total += n; });
          setProgress((pr) => ({ ...pr, rows: Math.max(pr.rows, total) }));
        };

        const buildFolder = async (folder) => {
          if (!on) return null;
          // EVERY INSERT GENERATOR THIS FOLDER HAS, in the order they are
          // written — that is what a structure means by several Insert rules
          // in one box. A work item two of them both find is drawn once, and
          // it keeps the position the FIRST one gave it.
          const inserts = (folder.inserts || []).map((q) => (q.zql || '').trim()).filter(Boolean);
          if (!inserts.length) return { folder, roots: [], byKey: new Map(), kidsOf: new Map(), depth: 0 };
          const folderFilter = andZql(queryFilter, folder.filterZql);
          setProgress((p) => ({ ...p, phase: `${folder.name} — inserting` }));
          const roots = [];
          const seenRoot = new Set();
          // A folder's insert queries keep their ORDER — the first to find a
          // work item decides where it sits — but they queue in the same pool
          // as everything else rather than waiting for one another.
          const inserted = await Promise.all(inserts.map((insert) => pool(() => fetchAll(
            `${andZql(insert, folderFilter)} order by key asc`, LOAD_FIELDS, null, cfg.maxRows,
          ))));
          inserted.forEach((got) => got.forEach((it) => {
            if (it && it.key && !seenRoot.has(it.key)) { seenRoot.add(it.key); roots.push(it); }
          }));
          progressOf.set(folder.id, roots.length);
          bumpRows();
          /* eslint-disable no-await-in-loop */
          // The admin's hand on this folder's contents: keys that are
          // ALWAYS in it whatever the query says, and keys that are never
          // in it however they are reached.
          const pinned = (folder.include || []).filter((k) => !roots.some((r) => r.key === k));
          if (pinned.length) {
            const extra = await fetchAll(`key in (${pinned.join(', ')})`, LOAD_FIELDS, null, cfg.maxRows);
            extra.forEach((x) => roots.push(x));
          }
          const banned = new Set(folder.exclude || []);
          const src = source || querySource((zql) => fetchAll(zql, LOAD_FIELDS, null, cfg.maxRows), {
            chunk: cfg.walkChunk, filterZql: folderFilter, pool, cache: loadCache,
          });
          const b = await expandFolder({
            roots,
            source: src,
            byParent: folder.expand.byParent,
            links: folder.expand.links,
            maxDepth: folder.expand.maxDepth,
            maxRows: cfg.maxRows,
            keep: banned.size ? (it) => !banned.has(it && it.key) : undefined,
            onProgress: (pr) => {
              if (!on) return;
              // Every folder is walking at once, so the running total is the
              // SUM of what they have each reached — not one folder's count
              // plus however much happened to be finished before it started.
              progressOf.set(folder.id, pr.total);
              bumpRows();
              setProgress((p) => ({ ...p, phase: `${folder.name} — level ${pr.depth}, ${pr.total.toLocaleString()} items` }));
            },
          });
          /* eslint-enable no-await-in-loop */
          // The filter, applied the way a structure applies it: a row it
          // rejects survives only as the route to something it accepts.
          progressOf.set(folder.id, b.byKey.size);
          bumpRows();
          return excluded
            // The prune answers with the tree; the DEPTH is a fact about the
            // walk that made it, so it is carried across by hand.
            ? { folder, ...pruneToMatches(b, new Set([...b.byKey.keys()].filter((k) => !excluded.has(k)))), depth: b.depth }
            : { folder, ...b };
        };

        // They come back in the order the structure writes them, whatever
        // order they finish in — the shape is the admin's, not the network's.
        const parts = (await Promise.all(cfg.folders.map(buildFolder))).filter(Boolean);
        if (!on) return;
        quietBusy.current = false;
        setBuilt(parts);
        // A FRESH LOAD, and only a fresh load, lays the tree out again. The
        // open/shut reset used to key on the folders' sizes, so adding one
        // work item (a create) or taking one out (a delete) read as a new
        // structure and shut every group the reader had opened.
        setLoadGen((g) => g + 1);
        setLoadInfo({
          seconds: (Date.now() - startedAt) / 1000,
          excluded: excluded ? excluded.size : null,
          filterNote,
          universe: universeCount,
          capped,
          folders: parts.map((p) => ({ name: p.folder.name, roots: p.roots.length, items: p.byKey.size, depth: p.depth })),
          // WHAT IT COST, in the only unit that matters here: round trips,
          // and how many of them the cache answered without one. Without
          // these two numbers "it feels slow" has nowhere to go.
          queries: loadCache.queries,
          saved: loadCache.saved,
          error: null,
        });
        setProgress({ rows: universeCount, phase: '' });
      } catch (e) {
        if (!on) return;
        const msg = (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'failed';
        // A quiet reload that fails leaves the tree it had; the reader's ↻
        // is still there.
        if (quiet) { setFillNote('A new work item could not be brought in — press ↻ to reload'); return; }
        setBuilt([]);
        setError(`The structure could not be built — ${msg}`);
        setLoadInfo({ universe: 0, capped: false, folders: [], error: msg });
      }
    })().finally(() => { if (on) quietBusy.current = false; });
    return () => { on = false; quietBusy.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, fieldsReady, fetchAll, loadKey, reloadTick, soloKey]);

  // Every work item the structure reached, once — what the filters run over.
  // Once the structure is up, warm the two things a first click would
  // otherwise wait for: each project's creation hierarchy, and the edit
  // shape of each (project, work type) on screen. Both are cached per
  // project, so this is a handful of requests no matter how big the tree.
  useEffect(() => {
    if (!built || !built.length) return;
    const sample = [];
    built.forEach((pt) => { let n = 0; pt.byKey.forEach((it) => { if (n < 40) { sample.push(it); n += 1; } }); });
    warmProjects(api, sample);
    prefetchEditMeta(api, sample.slice(0, 12));
  }, [built, api]);

  const allItems = useMemo(() => {
    const by = new Map();
    (built || []).forEach((p) => p.byKey.forEach((it, k) => { if (!by.has(k)) by.set(k, it); }));
    return by;
  }, [built]);
  // A work item a row names but no folder holds (a parent shown by name, a
  // header that is a work item) is found too — its cells read like any row's.
  const itemOf = useCallback((k) => allItems.get(k) || refItems.get(k) || null, [allItems, refItems]);

  // TABLE LINES AS ROWS (./lines.js): every folder that draws the lines of a
  // table field, its work items' lines — made once per load (and again when
  // the admin changes the rule), never fetched: the table is a field the
  // load already carries. The rule is read off the folder AS IT IS NOW, so a
  // change in admin mode shows without a reload once its field is loaded.
  const fieldIdOf = useCallback((n) => (n === 'duedate' ? 'duedate' : (resolvePseudo(n) || resolveField(n, fieldList || []))), [fieldList]);
  const lines = useMemo(() => linesIndex((built || []).map((p) => ({
    ...p, folder: (cfg.folders || []).find((f) => f.id === p.folder.id) || p.folder,
  })), { fieldIdOf }), [built, cfg.folders, fieldIdOf]);
  // every line of a work item, whichever folder drew it — what the search reads
  const linesByOwner = useMemo(() => {
    const m = new Map();
    lines.all.forEach((l) => { if (!m.has(l.line.of)) m.set(l.line.of, []); m.get(l.line.of).push(l); });
    return m;
  }, [lines]);
  // what the External database columns look up: the work items AND the lines
  const lookupItems = useMemo(() => {
    if (!lines.all.size) return allItems;
    const m = new Map(allItems);
    lines.all.forEach((l, k) => m.set(k, l));
    return m;
  }, [allItems, lines]);

  // EXTERNAL DATABASE columns: every value the shown lookup columns need,
  // asked of the External Database Sources at once (useLookups.js) — a fresh
  // load asks again, so a file changed in SharePoint shows after ↻.
  const lookupOf = useLookups({ cols: allCols, fieldIds, items: lookupItems, textsOf, slug: page && page.slug, epoch: loadGen });

  // ---- a column the load did not know about ------------------------------------
  // A reader adds "Priority" from the "+" and the page has never fetched
  // priority for anything. Rather than a reload — minutes, and their place in
  // the tree gone — the one field is asked for over every loaded work item,
  // a thousand keys a query, and patched in. The grid says which ids its
  // columns want (see onFieldsWanted in Grid.jsx); this remembers what it has
  // already fetched so the same field is never asked for twice.
  const fetchedExtra = useRef(new Set());
  const [fillNote, setFillNote] = useState('');
  const loadedFieldIds = useMemo(() => new Set(LOAD_FIELDS.split(',')), [LOAD_FIELDS]);
  const onFieldsWanted = useCallback(async (ids) => {
    if (!built || !built.length) return;
    const need = (ids || []).filter((id) => id && !NOT_FIELDS.includes(id) && !loadedFieldIds.has(id) && !fetchedExtra.current.has(id));
    if (!need.length) return;
    need.forEach((id) => fetchedExtra.current.add(id));
    const keys = [...allItems.keys()];
    setFillNote(`Filling ${need.length === 1 ? 'a new column' : `${need.length} new columns`} for ${keys.length.toLocaleString()} work items…`);
    try {
      const pool = makePool(FILTER_PARALLEL);
      const got = await Promise.all(chunkKeys(keys, FILTER_CHUNK).map((chunk) => pool(() => fetchAll(
        `key in (${chunk.join(', ')})`, need.join(','), null, MAX_CAP,
      ))));
      const byKey = new Map();
      got.forEach((rows) => (rows || []).forEach((r) => { if (r && r.key) byKey.set(r.key, r.fields || {}); }));
      const patch = (it) => {
        const f = byKey.get(it.key);
        if (!f) return it;
        const add = {};
        need.forEach((id) => { add[id] = f[id] === undefined ? null : f[id]; });
        return { ...it, fields: { ...it.fields, ...add } };
      };
      setBuilt((prev) => (prev || []).map((p) => {
        const nb = new Map();
        p.byKey.forEach((it, k) => nb.set(k, patch(it)));
        return { ...p, byKey: nb, roots: p.roots.map(patch) };
      }));
    } catch (e) {
      need.forEach((id) => fetchedExtra.current.delete(id));
      const msg = (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'failed';
      setSaveErrText(`The new column could not be filled: ${msg}`);
    } finally { setFillNote(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, allItems, loadedFieldIds, fetchAll]);

  // ---- the reader's filters ---------------------------------------------------
  const [search, setSearch] = useState('');
  const [zqlBox, setZqlBox] = useState('');
  const [zqlApplied, setZqlApplied] = useState('');
  // ZQL or plain words. The box is the same box; what it does with what you
  // type is not. See TEXT MODE below for what "everything" honestly means
  // on this site.
  const [searchMode, setSearchMode] = useState('zql');
  // THE QUERY BOX IS AS WIDE AS THE QUERY. It starts at a comfortable width
  // — twice the width of a search field, since a ZQL clause with two
  // conditions did not fit the first one — and stretches as the query is
  // typed, measured in characters of the monospace face it is set in, up
  // to the width of the toolbar, where the flex row clamps it. A long query
  // is read whole, never scrolled inside a box the size of a search field.
  const queryWidth = useMemo(() => Math.max(880, Math.round(400 + String(zqlBox || '').length * 7.9)), [zqlBox]);
  const [sel, setSel] = useState({});
  const [doneMode, setDoneMode] = useState(cfg.hideDone);
  useEffect(() => { setDoneMode(cfg.hideDone); }, [cfg.hideDone]);
  const [buttonsOn, setButtonsOn] = useState(() => new Set(cfg.buttons.filter((b) => b.on).map((b) => b.id)));
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The amber generator rows are an admin's tool, not decoration: they are
  // how the structure's own rules are read and (in admin mode) changed. A
  // reader has no use for them and no right to edit them, so the pin is not
  // even offered outside admin mode.
  const [showRules, setShowRules] = useState(cfg.showRules && adminMode);
  useEffect(() => { setShowRules(adminMode && cfg.showRules); }, [cfg.showRules, adminMode]);
  const [editRule, setEditRule] = useState(null);
  // The data is the page. Full screen takes the structure out of the
  // column of chrome above it and gives it the whole of its tab — Escape
  // brings the rest back. The app's navigation stays: the sheet covers the
  // tab's pane (the one this structure sits in), not the window, so the tab
  // bar and the top nav are still there to go elsewhere from.
  const [full, setFull] = useState(false);
  const rootRef = useRef(null);
  const [sheetHost, setSheetHost] = useState(null);
  const toggleFull = useCallback(() => {
    if (!full) setSheetHost((rootRef.current && rootRef.current.closest('.tab-pane')) || null);
    setFull((v) => !v);
  }, [full]);
  useEffect(() => {
    if (!full) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setFull(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [full]);
  const [vh, setVh] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
  useEffect(() => {
    const on = () => setVh(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const valsOf = useCallback((it, f) => {
    if (f.fieldId === 'status') return [statusOf(it) || '(none)'];
    if (f.fieldId === 'issuetype') return [typeOf(it) || '(none)'];
    const v = textsOf(it, f.fieldId);
    return v.length ? v : ['(none)'];
  }, []);
  const filterOptions = useMemo(() => {
    const out = {};
    filterDefs.forEach((f) => {
      const by = new Map();
      allItems.forEach((it) => valsOf(it, f).forEach((v) => by.set(v, (by.get(v) || 0) + 1)));
      out[f.id] = [...by.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0]))).map(([v, n]) => ({ value: v, label: v, hint: `${n}` }));
    });
    return out;
  }, [allItems, filterDefs, valsOf]);

  // The quick buttons and the ZQL box are QUERIES — they are answered by the
  // server over the keys the structure holds, never guessed at client-side.
  const [keySets, setKeySets] = useState({});
  const structKeys = useMemo(() => [...allItems.keys()], [allItems]);
  const activeButtons = useMemo(() => cfg.buttons.filter((b) => buttonsOn.has(b.id) && b.zql.trim()), [cfg.buttons, buttonsOn]);

  // TEXT MODE — and what it can honestly reach.
  //
  // This site's ZQL has three free-text targets: `summary`, `description`
  // and `text` — and `text` is summary ALONE (checked: `text ~ "fuselage"`
  // and `summary ~ "fuselage"` both answer 999, while `description ~`
  // answers 78). There is no comment field in the ZQL adapter at all, so
  // comments cannot be searched from here; that would be a change to
  // modules/database/zql, which is product, not a custom.
  //
  // So text mode does the most it can: summary OR description on the
  // SERVER, which reaches work items the page has not loaded a field for,
  // and on top of that every value the page HAS loaded is matched in the
  // browser. The two together are the honest meaning of "search the text".
  const textZql = useCallback((q) => {
    const t = String(q || '').trim().replace(/"/g, '\\"');
    if (!t) return '';
    return `(summary ~ "${t}" OR description ~ "${t}")`;
  }, []);
  const appliedQuery = useMemo(
    () => (searchMode === 'text' ? textZql(zqlApplied) : zqlApplied.trim()),
    [searchMode, zqlApplied, textZql],
  );
  const wantedZqls = useMemo(() => {
    const out = new Set();
    activeButtons.forEach((b) => out.add(b.zql.trim()));
    if (appliedQuery) out.add(appliedQuery);
    // A ZQL-bucket grouping is answered the same way a quick button is: the
    // server, over the structure's keys, a thousand at a time.
    groupZqls(cfg).forEach((z) => out.add(z));
    return [...out];
  }, [activeButtons, appliedQuery, cfg]);
  // A FILTER STAYS TRUE WHILE THE TREE CHANGES UNDER IT — and it costs only
  // what changed. Each answer remembers WHICH OBJECT it asked about for every
  // key (`asked`: key → the row as it was). Every change to a row — a create
  // from the "+" or the pop-up, an edit, a transition, another person's
  // change arriving live — replaces that row's object (the tree is never
  // mutated in place), so a cheap identity check over the loaded rows finds
  // exactly the rows the answer has not seen, and ONLY THOSE are asked again:
  // `key in (NEW-1, NEW-2) AND (<the filter>)`, one small query. The answer
  // used to be asked once and kept, so a Projected Income created under a
  // `issuetype = "Projected Income"` filter never appeared until ↻.
  //
  // No polling and no timer: nothing is asked unless a row changed. The
  // check is a walk of the loaded map comparing references (milliseconds at
  // thirty thousand rows); `asked` holds references to rows the page already
  // has, not copies. A full reload makes every row new, and is then asked in
  // full — which is what a reload is.
  const askingRef = useRef(new Set());
  useEffect(() => {
    if (!structKeys.length) return;
    const errorOf = (e) => (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'failed';
    // A THOUSAND KEYS A QUERY, FOUR QUERIES AT A TIME — see FILTER_CHUNK.
    // Two hundred at a time, one after another, was a hundred and
    // forty-five round trips per button on this structure, and the page
    // showed nothing for the whole of them.
    const ask = async (zql, keys) => {
      const hit = new Set();
      const pool = makePool(FILTER_PARALLEL);
      const parts = await Promise.all(chunkKeys(keys, FILTER_CHUNK).map((chunk) => pool(() => fetchAll(
        `key in (${chunk.join(', ')}) AND (${zql})`, 'key', null, MAX_CAP,
      ))));
      parts.forEach((rows) => (rows || []).forEach((r) => hit.add(r.key)));
      return hit;
    };
    const snapshot = allItems;
    wantedZqls.forEach((zql) => {
      const cur = keySets[zql];
      if (!cur) {
        setKeySets((k) => ({ ...k, [zql]: 'loading' }));
        ask(zql, [...snapshot.keys()])
          .then((keys) => setKeySets((k) => ({ ...k, [zql]: { keys, asked: snapshot } })))
          .catch((e) => setKeySets((k) => ({ ...k, [zql]: { error: errorOf(e) } })));
        return;
      }
      if (cur === 'loading' || !cur.keys || askingRef.current.has(zql)) return;
      const stale = [];
      snapshot.forEach((it, key) => { if (cur.asked.get(key) !== it) stale.push(key); });
      if (!stale.length) return;
      askingRef.current.add(zql);
      ask(zql, stale)
        .catch(() => null) // the rows stay as the last answer had them; the next change asks again
        .then((hit) => {
          // Cleared BEFORE the answer is stored, so the render it causes can
          // ask about any row that changed while this one was out.
          askingRef.current.delete(zql);
          setKeySets((k) => {
            const prev = k[zql];
            if (!prev || !prev.keys) return k;
            const keys = new Set(prev.keys);
            const asked = new Map(prev.asked);
            stale.forEach((key) => {
              asked.set(key, snapshot.get(key));
              if (hit) { if (hit.has(key)) keys.add(key); else keys.delete(key); }
            });
            return { ...k, [zql]: { keys, asked } };
          });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedZqls, keySets, fetchAll, allItems]);
  const setOf = useCallback((zql) => (zql && zql.trim() ? keySets[zql.trim()] : null), [keySets]);
  const zqlError = appliedQuery && setOf(appliedQuery) && setOf(appliedQuery).error;
  // The columns pinned at the left, and an admin's unpin from a heading.
  const frozenIds = useMemo(() => frozenIdsOf(cfg), [cfg]);
  const freezeColumn = useCallback((id, on) => setDraft((d0) => {
    const n = normalizeConfig(d0, defaults);
    const next = new Set(frozenIdsOf(n));
    if (on) next.add(id); else next.delete(id);
    return { ...d0, frozenColumns: n.columns.map((c) => c.id).filter((x) => next.has(x)) };
  }), [defaults]);

  // ZQL or plain words — the switch in front of the query box, either way.
  const queryModeSwitch = (
    <span style={{ display: 'inline-flex', flex: '0 0 auto', borderRadius: 999, overflow: 'hidden', background: ui.surface, border: `1px solid ${ui.border}` }}>
      {['zql', 'text'].map((m) => (
        <button key={m} onClick={() => { setSearchMode(m); setZqlApplied(''); }}
          title={m === 'zql' ? 'Write a ZQL query, with suggestions' : 'Type words — searches summary and description on the server, and every value this page has loaded'}
          style={{
            border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 10.5, fontWeight: 800,
            letterSpacing: '0.06em', padding: '6px 9px', lineHeight: 1,
            background: searchMode === m ? ui.primary : 'transparent',
            color: searchMode === m ? ui.primaryFg : ui.muted,
          }}>{m === 'zql' ? 'ZQL' : 'TEXT'}</button>
      ))}
    </span>
  );

  // "Filter my clones": the keys a clone run just made, and nothing else.
  const [cloneFilter, setCloneFilter] = useState(null);
  const anyFilter = !!(search.trim() || zqlApplied.trim() || doneMode !== 'all' || cloneFilter
    || Object.values(sel).some((s) => s && s.size)
    || cfg.buttons.some((b) => buttonsOn.has(b.id) !== !!b.on));
  const clearFilters = () => {
    setSearch(''); setSel({}); setZqlBox(''); setZqlApplied(''); setDoneMode(cfg.hideDone); setCloneFilter(null);
    setButtonsOn(new Set(cfg.buttons.filter((b) => b.on).map((b) => b.id)));
  };

  // WHAT THE BUTTONS SWITCHED ON NARROW (see buttonTests in ./structure.js):
  // `rows` prunes every row here, `roots` narrows the inserted rows in `view`.
  const btn = useMemo(() => buttonTests(activeButtons, { mode: cfg.buttonsMode, scope: cfg.buttonsScope, setOf }), [activeButtons, cfg.buttonsMode, cfg.buttonsScope, setOf]);
  // A LENS BUTTON (config.js: lens) HIDES ROWS, NOT WORK. With one on, the
  // rows on screen are narrowed by every button, but what the columns COUNT
  // under a row (Count, Σ, progress, "Count where…", a folder's span) is
  // narrowed by the other buttons only — "only the orders and their
  // assemblies" still shows an order's progress over its operations.
  const lensOn = activeButtons.some((b) => b.lens);
  const btnCount = useMemo(() => (lensOn ? buttonTests(activeButtons.filter((b) => !b.lens), { mode: cfg.buttonsMode, scope: cfg.buttonsScope, setOf }) : null), [lensOn, activeButtons, cfg.buttonsMode, cfg.buttonsScope, setOf]);
  const matches = useMemo(() => {
    const q = lower(search.trim());
    const zqlSet = appliedQuery ? setOf(appliedQuery) : null;
    const pending = btn.pending || (!!appliedQuery && (!zqlSet || zqlSet === 'loading'));
    // In text mode the server's answer is one half; the other is every
    // value this page has already loaded, matched here.
    const freeText = searchMode === 'text' ? lower(zqlApplied.trim()) : '';
    // A FILTER IN FORCE is not the same as "something differs from how the
    // page opened" (anyFilter — what Clear is offered for): a button that is
    // ON BY DEFAULT and narrows every row must narrow it from the first
    // paint, or its pressed pill sits over rows it never filtered.
    const active = anyFilter || !!btn.rows;
    const hit = new Set();
    // the keys the columns count (a lens on): every filter but the lenses
    const hitCount = btnCount ? new Set() : null;
    const countActive = !!btnCount && (!!btnCount.rows || !!(search.trim() || zqlApplied.trim() || doneMode !== 'all' || cloneFilter || Object.values(sel).some((s) => s && s.size)));
    if (pending) return { hit, pending, active, hitCount, countActive };
    allItems.forEach((it, key) => {
      if (cloneFilter && !cloneFilter.has(key)) return;
      if (zqlSet && zqlSet.keys && !zqlSet.keys.has(key)) {
        if (!freeText || !matchesLoadedText(it, freeText)) return;
      }
      const shown = !btn.rows || btn.rows(key);
      const counted = !!hitCount && (!btnCount.rows || btnCount.rows(key));
      if (!shown && !counted) return;
      if (doneMode === 'hide' && isDone(it)) return;
      if (doneMode === 'only' && !isDone(it)) return;
      for (const f of filterDefs) {
        const s = sel[f.id];
        if (s && s.size) { const vs = valsOf(it, f); if (!vs.some((v) => s.has(v))) return; }
      }
      // a work item matches by its own text — or by a line of its table (a
      // part number typed in the box finds every work item that uses it)
      if (q && !lower(`${key} ${(it.fields && it.fields.summary) || ''} ${statusOf(it)} ${typeOf(it)}`).includes(q)
        && !(linesByOwner.get(key) || []).some((l) => lineHas(l, q))) return;
      if (shown) hit.add(key);
      if (counted) hitCount.add(key);
    });
    return { hit, pending, active, hitCount, countActive };
  }, [allItems, btn, btnCount, setOf, appliedQuery, searchMode, zqlApplied, filterDefs, sel, valsOf, search, doneMode, anyFilter, cloneFilter, linesByOwner]);

  // A row survives if it matches or anything under it does.
  //
  // WHILE THE SERVER IS STILL ANSWERING, THE STRUCTURE STAYS ON SCREEN. It
  // used to be emptied the moment a button was pressed and stay empty until
  // the last query came back — which read as "the filter deleted
  // everything". The rows are left as they are and the toolbar says the
  // filter is being applied; the pruned tree replaces them when it is real.
  //
  // A grid whose buttons narrow the INSERTS (buttonsScope 'roots') first
  // keeps only the inserted rows the buttons pass, with everything under
  // them; the rest of the filters then prune that as usual.
  const view = useMemo(() => {
    if (!built) return [];
    if (btn.pending) return built;
    let parts = built;
    if (btn.roots) parts = parts.map((p) => ({ folder: p.folder, depth: p.depth, ...keepRoots(p, btn.roots) }));
    if (!matches.active || matches.pending) return parts;
    return parts.map((p) => ({ folder: p.folder, ...pruneToMatches(p, matches.hit) }));
  }, [built, matches, btn]);
  // …and, with a lens on, the tree the columns COUNT: the same narrowing
  // without the lenses (null = count what is shown). Part for part with
  // `view`, which is built from the same `built` in the same order.
  const countView = useMemo(() => {
    if (!built || !btnCount || btn.pending || matches.pending) return null;
    let parts = built;
    if (btnCount.roots) parts = parts.map((p) => ({ folder: p.folder, depth: p.depth, ...keepRoots(p, btnCount.roots) }));
    if (!matches.countActive) return parts;
    return parts.map((p) => ({ folder: p.folder, ...pruneToMatches(p, matches.hitCount) }));
  }, [built, matches, btn, btnCount]);

  // A FILTER KEEPS THE TREE AS THE READER HAS IT (the owner, 2026-10-22: "any
  // filter auto uncollapses — NOT RIGHT"). A filter used to open everything
  // it found, so a grid filtered to one project — another page's "See in
  // Structure", a ZQL, a quick button — stood open at every level, hundreds
  // of rows deep. The pruned tree (the matches and the rows that lead to
  // them) is now drawn with the reader's own open / shut: the folders open,
  // the rows in them shut unless the reader opened them; Expand all is one
  // click away. `searching` = a filter is in force: the reader's place is
  // not saved while one is.
  const searching = !!(search.trim() || zqlApplied.trim() || cloneFilter
    || Object.values(sel).some((s) => s && s.size)
    || cfg.buttons.some((b) => buttonsOn.has(b.id) && !b.on));

  // ---- open / shut ------------------------------------------------------------
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = useCallback((path) => setCollapsed((prev) => {
    const n = new Set(prev);
    if (n.has(path)) n.delete(path); else n.add(path);
    return n;
  }), []);

  const startFieldId = useMemo(() => {
    const c = (cfg.columns || []).find((x) => x.kind === 'date' && x.field && x.field !== 'duedate');
    return c ? fieldIds[c.id] : '';
  }, [cfg.columns, fieldIds]);

  // WHICH GROUP A WORK ITEM IS IN, for each of the five kinds a grouping can
  // be (see ./grouping.js). Formulas are compiled once per grouping and
  // kept; the ZQL buckets read the same answer store as the quick buttons.
  const compiledFormulas = useMemo(() => {
    const o = {};
    (cfg.folders || []).forEach((f) => (f.groups || []).forEach((g) => { if (g.kind === 'formula') o[`${f.id}:${g.id}`] = compileFormula(g.formula); }));
    return o;
  }, [cfg.folders]);
  const todayIsoNow = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }, [loadGen]);
  const resolveName = useCallback((name) => resolvePseudo(name) || resolveField(name, fieldList || []), [fieldList]);
  // A grouping with a header icon (g.iconType) puts that work type's icon on
  // every header that has no look of its own — see normalizeGroup. One
  // stand-in item per type name, so the headers share it.
  const iconSamples = useRef(new Map());
  const iconSampleOf = (name) => {
    if (!iconSamples.current.has(name)) iconSamples.current.set(name, { key: '', fields: { issuetype: { name } } });
    return iconSamples.current.get(name);
  };
  // A HEADER THAT IS A WORK ITEM: its key, its summary, the work item itself
  // (one the structure holds, or one read beside it — refItems). One that
  // could not be read (not permitted, not there) is a plain header with its key.
  const itemHeader = useCallback((k) => {
    const item = itemOf(k);
    const summary = item && item.fields && item.fields.summary ? String(item.fields.summary) : '';
    return item ? { key: k, label: summary || k, item } : { key: k, label: k };
  }, [itemOf]);
  const rawGroupValueOf = useCallback((it, g, folderId) => {
    if (!g) return '(none)';
    // A LINK GROUPING: under the work item that names this one by the link —
    // under each, when there are several; "No links" when there is none.
    if (g.kind === 'link') {
      const keys = linkedParentKeys(it, { type: g.linkType, relation: g.linkRelation });
      return keys.length ? keys.map(itemHeader) : { key: '~nolinks', label: 'No links' };
    }
    const fid = groupIds[`${folderId}:${g.id}`];
    if (g.kind === 'date') return dateBucket(fid && it && it.fields ? it.fields[fid] : null, g.grain, todayIsoNow);
    if (g.kind === 'preset') return presetGroup(it, g.preset, todayIsoNow);
    if (g.kind === 'zql') return zqlGroup(it, g.buckets, setOf, g.other);
    if (g.kind === 'formula') return formulaGroup(it, compiledFormulas[`${folderId}:${g.id}`], { resolve: resolveName, todayIso: todayIsoNow });
    // GROUPED BY A WORK ITEM — the Parent, an Epic Link: the header IS that
    // work item (see itemHeader), as a structure's "Group by Epic" draws it.
    if (fid && refFieldIds.has(fid)) {
      const keys = refKeysIn(it && it.fields ? it.fields[fid] : null);
      if (!keys.length) return '(none)';
      return keys.length === 1 ? itemHeader(keys[0]) : keys.map(itemHeader);
    }
    // A FIELD GROUPING'S HEADERS: the admin's own order first (g.order),
    // the rest after them A to Z; Status, unless told otherwise, goes
    // To do → In progress → Done and A to Z inside each.
    // Grouped by work type or status, the header wears its icon / lozenge
    // (see the group row in Grid.jsx). The order is untouched by it.
    const look = fid === 'status' ? { look: 'status', sample: it } : (fid === 'issuetype' ? { look: 'type', sample: it } : null);
    const one = (label) => {
      const at = orderIn(g.order, label);
      if (at != null) return { key: label, label, order: at, ...look };
      if (fid === 'status') return { key: label, label, order: 1e6 + statusRank(it), ...look };
      if (g.order && g.order.length) return { key: label, label, order: 2e6, ...look };
      return look ? { key: label, label, ...look } : label;
    };
    const labels = fid === 'status' ? [statusOf(it)] : (fid === 'issuetype' ? [typeOf(it)] : textsOf(it, fid));
    const named = [...new Set(labels.filter(Boolean))];
    if (!named.length) return '(none)';
    // A MULTI-SELECT HOLDING TWO VALUES puts the work item under both, as a
    // structure does (one row under each; Count still counts it once).
    return named.length === 1 ? one(named[0]) : named.map(one);
  }, [groupIds, todayIsoNow, setOf, compiledFormulas, resolveName, refFieldIds, itemHeader]);
  // WITHOUT BEING ASKED, when the grouping's FIELD has the name of a WORK
  // TYPE — an "Asset" field on a site with an "Asset" work type: its values
  // are the names of those work items, so its headers wear that type's icon.
  // An admin's own choice wins; "none" (iconType '-') turns it off.
  const headerIconOf = useCallback((g) => {
    if (!g) return '';
    if (g.iconType === '-') return '';
    if (g.iconType) return g.iconType;
    if (g.kind !== 'field' || !g.field || !workTypes) return '';
    const t = workTypes.get(String(g.field).trim().toLowerCase());
    return t && t.name ? t.name : '';
  }, [workTypes]);
  // A header's picture: the admin's work type icon, or none at all (see
  // normalizeGroup). A header that is a work item wears its own.
  const dressGroup = useCallback((v0, g) => {
    let v = v0;
    if (v && typeof v === 'object' && v.item) return v;
    // "None" (iconType '-') means NO picture on these headers at all: a grouping by project then
    // draws no project avatar either — a grid that wants the project's name only
    if (g && g.iconType === '-' && v && typeof v === 'object' && v.icon) { v = { ...v }; delete v.icon; }
    const icon = headerIconOf(g);
    if (!icon) return v;
    const o = v && typeof v === 'object' ? v : { key: String(v), label: String(v) };
    if (o.look || String(o.key).startsWith('~') || o.key === '(none)') return v;
    return { ...o, look: 'type', sample: iconSampleOf(icon) };
  }, [headerIconOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const groupValueOf = useCallback((it, g, folderId) => {
    const raw = rawGroupValueOf(it, g, folderId);
    // several groups (a multi-select, two link parents) are each dressed
    return Array.isArray(raw) ? raw.map((v) => dressGroup(v, g)) : dressGroup(raw, g);
  }, [rawGroupValueOf, dressGroup]);

  // AN UNSET GROUPING IS NOT A GROUPING YET. It stays in the config so its
  // amber row can be edited, but the rows are bracketed only by the ones
  // that are set up — otherwise every row would sit under one "(none)".
  // THE FOLDER AS IT IS NOW, not as it was when the rows were loaded. The
  // loaded parts carry the folder object the load ran with; a grouping, a
  // sort or a name changed in admin mode since then is on the draft, and
  // none of those needs a reload (the rows are the same — only their
  // bracketing, order and heading change). Reading the loaded folder here
  // was why a grouping added after the load never appeared: neither its
  // amber rule nor its group rows were ever computed from it.
  const parts = useMemo(() => (view || []).map((p, i) => {
    const live = (cfg.folders || []).find((f) => f.id === p.folder.id) || p.folder;
    // what this folder's rows COUNT under them: the lens-free tree when a lens is on
    const counted = countView && countView[i] && countView[i].folder.id === p.folder.id ? countView[i] : p;
    // The engine sorts by id; a field sort is resolved against the site here.
    const folder = sortFieldOf(live.sort) ? { ...live, sort: engineSort(live.sort) } : live;
    const groups = (folder.groups || []).filter(groupIsSet);
    // Its TABLE LINES, drawn first under each work item. While the search box
    // holds text, a work item that matched through its lines shows only the
    // lines that hold the text; one that matched by its own name shows them all.
    const mine = lines.byFolder.get(folder.id) || null;
    const q = String(search || '').trim().toLowerCase();
    const linesOf = mine ? (k) => {
      const ls = mine.get(k) || NO_LINES;
      if (!q || !ls.length) return ls;
      const it = p.byKey.get(k);
      if (it && `${k} ${(it.fields && it.fields.summary) || ''} ${statusOf(it)} ${typeOf(it)}`.toLowerCase().includes(q)) return ls;
      return ls.filter((l) => lineHas(l, q));
    } : null;
    // GROUP THE CHILDREN (folder.groupChildren): under every row, its children
    // of the named work types bracketed by the field's value ("(none)" when
    // blank); children of other types are not grouped (null).
    const cgRule = folder.groupChildren;
    const cgField = cgRule && cgRule.field ? resolveName(cgRule.field) : '';
    const cgTypes = cgRule && cgRule.types && cgRule.types.length ? new Set(cgRule.types.map((t) => String(t).trim().toLowerCase())) : null;
    // ITS HEADERS ARE DRESSED AS A GROUPING'S ARE: by work type, the type's
    // icon; by status, its lozenge; by a field named like a work type, that
    // type's icon — never the plain square of a header with no look of its own.
    const cgIcon = cgRule ? headerIconOf({ kind: 'field', field: cgRule.field }) : '';
    const childGroup = cgField ? {
      field: cgRule.field,
      of: (it) => {
        if (!it || (cgTypes && !cgTypes.has(String(typeOf(it) || '').trim().toLowerCase()))) return null;
        const v = cgField === 'status' ? statusOf(it) : (cgField === 'issuetype' ? typeOf(it) : textsOf(it, cgField)[0]);
        if (!v) return '(none)';
        const label = String(v);
        if (cgField === 'issuetype') return { key: label, label, look: 'type', sample: it };
        if (cgField === 'status') return { key: label, label, look: 'status', sample: it };
        if (cgIcon) return { key: label, label, look: 'type', sample: iconSampleOf(cgIcon) };
        return label;
      },
    } : null;
    return {
      folder,
      groups,
      roots: p.roots,
      byKey: p.byKey,
      kidsOf: p.kidsOf,
      counter: makeCounter(counted.byKey, counted.kidsOf),
      groupsOf: (it, depth) => groupValueOf(it, groups[depth], folder.id),
      // the grouping callback BY GROUPING, whatever level it groups on
      groupOf: (it, g) => groupValueOf(it, g, folder.id),
      // the level sort rules, their fields resolved for the engine (sortKeysAt)
      sorts: (live.sorts || []).map((s) => ({ ...s, by: engineSort(s.by) })),
      linesOf,
      childGroup,
    };
  }), [view, countView, groupValueOf, cfg.folders, engineSort, lines, search, resolveName, headerIconOf]); // eslint-disable-line react-hooks/exhaustive-deps

  const paths = useMemo(() => (built ? allPaths({ parts, sortRoot: sortRootEff, startFieldId }) : []), [built, parts, sortRootEff, startFieldId]);
  // WHAT THE STRUCTURE OPENS TO BY DEFAULT: folders open, their contents
  // shut, unless a folder says otherwise.
  const defaultShut = useCallback((all) => {
    const shut = new Set();
    all.forEach((p) => {
      // The folder a row is IN is the last folder segment of its path —
      // folders nest, so the first segment may be a grandparent. The level
      // that "open N levels" counts is the one from that folder (rel).
      const segs = String(p.path).split('/');
      const fseg = segs.slice().reverse().find((s) => s.startsWith('f:'));
      const folderId = fseg ? fseg.slice(2) : '';
      const folder = (cfg.folders || []).find((f) => f.id === folderId);
      const depth = folder && folder.openDepth != null ? folder.openDepth : cfg.openDepth;
      const level = p.rel != null ? p.rel : p.level;
      if (!p.isFolder && level >= depth) shut.add(p.path);
    });
    return shut;
  }, [cfg.folders, cfg.openDepth]);
  // BACK WHERE THE READER WAS. A reload — theirs, or a deploy's — used to
  // land the structure the way it is configured to, with the reader's open
  // folders and their place on the page gone. Both are kept in this
  // browser, per grid: not the whole open/shut set (a row that arrives
  // later would then land open), but the reader's EXCEPTIONS to the default
  // — what they opened that would be shut, what they shut that would be
  // open — and how far down they were. Nothing is kept while a filter is in
  // force: the rows are a pruned tree then, and the place is the whole one's.
  const placeKey = `grids.${storeKey}.place`;
  const readPlace = () => {
    try { const raw = localStorage.getItem(placeKey); const p = raw ? JSON.parse(raw) : null; return p && typeof p === 'object' ? { open: p.open || [], shut: p.shut || [], top: Number(p.top) || 0 } : null; } catch { return null; }
  };
  const [restoreTop, setRestoreTop] = useState(null);   // { top, token } for the grid, once per load
  const restoredGen = useRef(0);
  const topRef = useRef(0);
  useEffect(() => {
    // ONLY on a fresh load: a create or a delete changes the rows, not the
    // reader's place in them.
    if (!loadGen || !built) return;
    const all = allPaths({ parts, sortRoot: sortRootEff, startFieldId });
    const shut = defaultShut(all);
    const place = readPlace();
    if (place) {
      const known = new Set(all.map((p) => p.path));
      place.open.forEach((p) => { if (known.has(p)) shut.delete(p); });
      place.shut.forEach((p) => { if (known.has(p)) shut.add(p); });
      topRef.current = place.top;
      if (place.top > 0) setRestoreTop({ top: place.top, token: loadGen });
    }
    setCollapsed(shut);
    restoredGen.current = loadGen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadGen, cfg.openDepth]);
  const savePlace = useCallback(() => {
    if (!built || restoredGen.current !== loadGen || searching) return;
    const all = allPaths({ parts, sortRoot: sortRootEff, startFieldId });
    const shutByDefault = defaultShut(all);
    const open = []; const shutNow = [];
    all.forEach((p) => {
      const isShut = collapsed.has(p.path);
      if (isShut && !shutByDefault.has(p.path)) shutNow.push(p.path);
      else if (!isShut && shutByDefault.has(p.path)) open.push(p.path);
    });
    try { localStorage.setItem(placeKey, JSON.stringify({ open, shut: shutNow, top: Math.round(topRef.current || 0), at: Date.now() })); } catch { /* storage may be blocked */ }
  }, [built, loadGen, searching, parts, sortRootEff, startFieldId, defaultShut, collapsed, placeKey]);
  const savePlaceRef = useRef(savePlace);
  useEffect(() => { savePlaceRef.current = savePlace; }, [savePlace]);
  // The open/shut set is kept as it changes; the scroll position as it
  // settles; and both when the page is left, whatever is pending.
  useEffect(() => {
    const t = setTimeout(() => savePlaceRef.current(), 300);
    return () => clearTimeout(t);
  }, [collapsed, savePlace]);
  const scrollSaveTimer = useRef(null);
  const onScrolled = useCallback((top) => {
    topRef.current = top;
    if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
    scrollSaveTimer.current = setTimeout(() => { scrollSaveTimer.current = null; savePlaceRef.current(); }, 400);
  }, []);
  useEffect(() => {
    const flush = () => savePlaceRef.current();
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('pagehide', flush); if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current); };
  }, []);
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(paths.map((p) => p.path)));
  // Memoised: `paths` can be tens of thousands long, and this would
  // otherwise walk all of it on every render.
  const allOpen = useMemo(() => paths.length > 0 && paths.every((p) => !collapsed.has(p.path)), [paths, collapsed]);
  const allShut = useMemo(() => paths.length > 0 && paths.every((p) => collapsed.has(p.path)), [paths, collapsed]);

  // ---- the rules, printed the way a structure prints them ---------------------
  // Each printed rule carries an `edit` descriptor naming the rule it is, so
  // clicking the line opens that rule and nothing else — the way a structure
  // behaves. Without it the amber rows would be a legend; with it they are
  // the structure's controls.
  const sortName = (id) => (SORT_NAMES[id] || sortFieldOf(id) || id);
  // The amber rows are the structure's controls, so ADDING is one of them.
  // A structure that can only be edited where it already has a rule cannot
  // grow a folder or a second grouping — the admin would be back in the
  // settings page, which is the thing this replaces. So each scope ends
  // with its own "add" line: one under the structure for a new folder, one
  // inside every folder for a new rule.
  const rules = useMemo(() => (!showRules ? null : {
    top: [
      ...(cfg.filterZql.trim()
        ? [{ icon: '▼', label: 'Filter work items:', value: cfg.filterZql, edit: { what: 'filter' }, remove: true }]
        : [{ icon: '▼', label: 'Filter work items:', value: '(none — click to add one)', edit: { what: 'filter' } }]),
      { icon: '⇅', label: 'Sort by', value: sortName(cfg.sortRoot), edit: { what: 'sortRoot' } },
      { icon: '＋', label: 'Add a folder', value: '', add: true, edit: { what: 'addFolder' } },
    ],
    folder: (f) => [
      ...(f.expand.links || []).map((l, i) => ({
        icon: '⛓', label: `Add work items linked by ${l.type}: parent “${l.relation}” children`, value: '',
        edit: { what: 'link', folderId: f.id, index: i }, remove: true,
      })),
      ...(f.expand.byParent ? [{ icon: '⛓', label: 'Child work items using the Parent field', value: '', edit: { what: 'parent', folderId: f.id }, remove: true }] : []),
      ...(f.expand.lines ? [{
        icon: '▤', label: 'Table lines as rows:',
        value: f.expand.lines.field
          ? `“${f.expand.lines.field}” under ${(f.expand.lines.types || []).length ? f.expand.lines.types.join(', ') : 'every work type'} — each line a ${f.expand.lines.label}`
          : '(no field yet — click to choose one)',
        edit: { what: 'lines', folderId: f.id }, remove: true,
      }] : []),
      ...(f.groups || []).map((g, i) => ({ icon: '▦', label: 'Group by', value: describeGroup(g), edit: { what: 'group', folderId: f.id, index: i }, remove: true })),
      ...(f.groupChildren ? [{
        icon: '▦', label: 'Group the children by',
        value: f.groupChildren.field
          ? `“${f.groupChildren.field}”${(f.groupChildren.types || []).length ? ` — ${f.groupChildren.types.join(', ')} under every row` : ' — under every row'}`
          : '(no field yet — click to choose one)',
        edit: { what: 'childGroups', folderId: f.id }, remove: true,
      }] : []),
      // The sort as the CONFIG names it — the part's folder carries the
      // engine's resolved `fieldid:` form, which is no name for a person.
      ...(() => {
        // THE FOLDER'S SORT, then its LEVEL rules in the order they run — the
        // config's names, never the engine's resolved ids.
        const cf = (cfg.folders || []).find((x) => x.id === f.id) || f;
        const rulesHere = cf.sorts || [];
        return [
          { icon: '⇅', label: 'Sort by', value: `${sortName(cf.sort)}${rulesHere.length ? ' — every level (the level rules below decide first)' : ''}`, edit: { what: 'sort', folderId: f.id } },
          ...rulesHere.map((s, i) => ({
            icon: '⇅', label: 'Sort by',
            value: `${sortName(s.by)} ${s.desc ? '↓' : '↑'} — ${s.levelTo > s.levelFrom ? `levels ${s.levelFrom} to ${s.levelTo >= 20 ? 'the last' : s.levelTo}` : `level ${s.levelFrom}`}`,
            edit: { what: 'levelSort', folderId: f.id, index: i }, remove: true,
          })),
        ];
      })(),
      ...(f.filterZql.trim() ? [{ icon: '▼', label: 'Filter work items:', value: f.filterZql, edit: { what: 'folderFilter', folderId: f.id }, remove: true }] : []),
      ...(f.inserts || []).map((q, i) => ({
        icon: '✛',
        label: (f.inserts.length > 1 ? `Insert work items (${i + 1}):` : 'Insert work items:'),
        value: q.zql || '(none yet — click to write one)',
        edit: { what: 'insert', folderId: f.id, index: i }, remove: true,
      })),
      // The folder ITSELF — its name, its icon, whether it is bare, where it
      // sits. The editor had all of it; nothing on the page opened it.
      { icon: '✎', label: 'Folder — name, icon, bare, where it sits', value: '', add: true, edit: { what: 'folder', folderId: f.id }, remove: true },
      { icon: '＋', label: 'Add a folder inside this one', value: '', add: true, edit: { what: 'addFolder', parentId: f.id } },
      { icon: '＋', label: 'Add a rule to this folder', value: '', add: true, edit: { what: 'addRule', folderId: f.id } },
    ],
  }), [showRules, cfg.filterZql, cfg.sortRoot, cfg.folders]);

  // THE BIN BESIDE AN AMBER LINE removes that rule from the draft, through the
  // same withoutRule its editor's Remove uses; a folder asks first. Nothing
  // is gone for anybody until Save.
  const onRemoveRule = useCallback((edit) => {
    const out = withoutRule(draft, edit);
    if (!out) return;
    // eslint-disable-next-line no-alert
    if (out.folder && !window.confirm(removeFolderQuestion(out.folder))) return;
    setDraft(out.draft);
  }, [draft]);

  // A FOLDER IS MOVED BY DRAGGING ITS ROW onto the folder it should sit in,
  // or onto the structure's own rules at the top to put it back at the top.
  // The grid does the dragging; the page knows the config. A folder cannot
  // go inside itself or inside one of its own sub-folders.
  const moveFolder = useCallback((id, parentId) => {
    setDraft((d0) => {
      const c0 = normalizeConfig(d0, defaults);
      const inside = parentId || null;
      if (inside && (inside === id || descendantFolderIds(c0.folders, id).has(inside))) return d0;
      const cur = c0.folders.find((f) => f.id === id);
      if (!cur || (cur.parentId || null) === inside) return d0;
      return { ...c0, folders: c0.folders.map((f) => (f.id === id ? { ...f, parentId: inside } : f)) };
    });
  }, [defaults]);

  const flatRows = useMemo(() => (built ? flattenStructure({
    parts, collapsed, sortRoot: sortRootEff, startFieldId, rules,
  }) : []), [built, parts, collapsed, sortRootEff, startFieldId, rules]);

  // THE TIMELINE DRAWS THE ROWS THE GRID DRAWS — it is a pane of the
  // structure, not a chart beside it. All it needs is a start, a due and
  // whether the work is finished, so those are attached here rather than
  // inside the renderer, and only when the timeline is on: a folder's span
  // is a walk over its whole branch, and there is no reason to pay for it
  // while nobody is looking at a bar.
  //
  // A folder or a group has no dates of its own, so it takes the SPAN of
  // everything underneath — earliest start to latest due — which is what
  // makes a shut folder still say something on the timeline.
  // AN AGGREGATE'S SPAN IS COMPUTED WHEN IT IS DRAWN, NOT WHEN IT IS BUILT.
  //
  // This used to walk every folder's and every group's `countKeys` up front:
  // on this structure that is a pass over ~28 500 work items per aggregate
  // row, redone on every filter, every opened folder and every saved edit —
  // and then thirty of those rows were actually on screen. It was the other
  // half of "the Gantt makes everything slow".
  //
  // A work item's own dates ARE cheap (two field reads), so those stay here:
  // the chart's WINDOW needs the two ends of the whole structure, and the
  // ends of the items are the ends of the aggregates by definition.
  // Everything else is looked up lazily, once per row, and remembered.
  const rows = useMemo(() => {
    if (!ganttOn) return flatRows;
    const dateOf = (it, id) => (it && it.fields && it.fields[id] ? String(it.fields[id]).slice(0, 10) : '');
    return flatRows.map((r) => {
      if (r.kind === 'rule' || !r.it) return r;
      return {
        ...r,
        start: startFieldId ? dateOf(r.it, startFieldId) : '',
        due: dateOf(r.it, 'duedate'),
        done: isDone(r.it),
      };
    });
  }, [flatRows, ganttOn, startFieldId]);

  // THE CHART'S WINDOW IS THE WHOLE STRUCTURE'S, not the screen's. Read off
  // the rows on screen, it moved every time a folder opened — the axis
  // rescaled under the reader — and a shut folder's bar, which spans work
  // that is not on screen, began before the window did and ran under the
  // columns. Every loaded work item is in hand, so the window is read off
  // all of them: one pass over two field reads each, recomputed only when
  // the structure itself changes, and every aggregate's span is inside it
  // by construction.
  const dateWindow = useMemo(() => {
    if (!ganttOn) return null;
    const dateOf = (it, id) => (it && it.fields && it.fields[id] ? String(it.fields[id]).slice(0, 10) : '');
    const list = [];
    allItems.forEach((it) => list.push({ start: startFieldId ? dateOf(it, startFieldId) : '', due: dateOf(it, 'duedate') }));
    return windowOf(list);
  }, [ganttOn, allItems, startFieldId]);

  // The lazy half. Keyed by the row's path, cleared whenever the rows or the
  // loaded work items change, so a saved date never leaves a stale span.
  const spanCache = useRef(new Map());
  useEffect(() => { spanCache.current = new Map(); }, [rows, built]);
  const spanOfRow = useCallback((r) => {
    if (!r || r.it || r.kind === 'rule') return null;
    const hit = spanCache.current.get(r.rowId);
    if (hit) return hit;
    const dateOf = (it, id) => (it && it.fields && it.fields[id] ? String(it.fields[id]).slice(0, 10) : '');
    let lo = ''; let hi = ''; let open = false;
    (r.countKeys || EMPTY_SET).forEach((k) => {
      const it = itemOf(k);
      if (!it) return;
      if (!isDone(it)) open = true;
      const st = startFieldId ? dateOf(it, startFieldId) : '';
      const du = dateOf(it, 'duedate');
      if (st) { if (!lo || st < lo) lo = st; if (!hi || st > hi) hi = st; }
      if (du) { if (!lo || du < lo) lo = du; if (!hi || du > hi) hi = du; }
    });
    const out = { start: lo, due: hi, done: !open };
    spanCache.current.set(r.rowId, out);
    return out;
  }, [itemOf, startFieldId]);

  // ---- one work item, on its own ----------------------------------------------
  //
  // MIDDLE-CLICKING A WORK ITEM OPENS ITS OWN SCREEN, FULL WINDOW, and
  // nothing else — not a search results page carrying one row. The link is
  // this page's own URL with ?item=KEY, so it needs no new route, and the
  // browser's own affordances
  // come free: middle click, Ctrl/Cmd-click, Copy link, and a visible
  // destination on hover.
  //
  // When that parameter is present the page does NOT build the structure at
  // all. The reader asked for one work item; making them wait through a
  // twenty-eight-thousand-row walk to see it would be absurd.
  const itemHref = useCallback((key) => {
    if (typeof window === 'undefined') return null;
    return `${window.location.pathname}?item=${encodeURIComponent(key)}`;
  }, []);
  // ---- editing ----------------------------------------------------------------
  const [popupItem, setPopupItem] = useState(null);
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const [saveErrText, setSaveErrText] = useState(null);
  const patchLoaded = useCallback((key, fields) => {
    setBuilt((prev) => (prev || []).map((p) => {
      if (!p.byKey.has(key)) return p;
      const byKey = new Map(p.byKey);
      const it = byKey.get(key);
      byKey.set(key, { ...it, fields: { ...it.fields, ...fields } });
      const roots = p.roots.map((r) => (r.key === key ? { ...r, fields: { ...r.fields, ...fields } } : r));
      return { ...p, byKey, roots };
    }));
    setRefItems((prev) => {
      if (!prev.has(key)) return prev;
      const m = new Map(prev);
      const it = m.get(key);
      m.set(key, { ...it, fields: { ...it.fields, ...fields } });
      return m;
    });
  }, []);
  // WHAT IS WRITTEN AND WHAT IS NOW BELIEVED ARE NOT THE SAME OBJECT, and
  // assuming they were is the whole of the "I edited it and the cell went to a
  // dash" bug. A select is written as { id } and read as { id, value }; a user
  // is written as { accountId } and read with a displayName. Patching the
  // page's copy with the write shape left a value every reader — the cell, the
  // picker's tick, the Excel export — asks the wrong questions of, so they all
  // saw nothing, while the change sat perfectly safely on the server and
  // showed up in the work item's history.
  //
  // So a caller that knows better hands over `display` as well. A date, a
  // number and a summary are the same in both shapes and pass nothing.
  //
  // Resolves true when the write landed, false when it did not — so a caller
  // with something to do AFTER a save (the dependency push below) does it
  // only for a change that is really on the server.
  const saveField = useCallback(async (it, fields, display) => {
    if (!it || !it.key) return false;
    setSaveErrText(null);
    setSavingKeys((prev) => new Set(prev).add(it.key));
    try {
      await api.updateIssue(it.key, fields);
      patchLoaded(it.key, display || fields);
      return true;
    } catch (e) {
      const msg = (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'failed';
      setSaveErrText(`${it.key} — the change was not saved: ${msg}`);
      return false;
    } finally {
      setSavingKeys((prev) => { const n = new Set(prev); n.delete(it.key); return n; });
    }
  }, [api, patchLoaded]);

  // ---- dependencies: the work that follows ------------------------------------
  // The grid's dependency rules (Page settings), the same ones the arrows are
  // drawn from. A grid with none draws no arrows, offers no link dots and
  // moves nothing.
  const depRules = cfg.gantt && cfg.gantt.dependencies;
  const hasDeps = !!(depRules && Object.keys(depRules).length);

  // WHEN DATES MOVE OR A LINK IS DRAWN, WHAT DEPENDS ON IT FOLLOWS — LATER.
  // A successor that would now start (or finish) too early is moved later by
  // exactly enough, and so on down the chain; nothing is ever moved earlier,
  // and nothing the change did not reach is touched (see ./schedule). Done
  // work stays where it is. Every work item the page holds is weighed, not
  // only the rows on screen, so a successor in a shut folder follows too.
  //
  // Each move is an ordinary write through saveField — the same request a
  // drag makes — one at a time so a long chain cannot flood the server, and
  // nothing is re-read afterwards: the page's copy is patched as each lands.
  // The plan first, apart from the writes, so a link can be weighed BEFORE it
  // is made (see onLink). { updates, error } — pushLater's answer.
  const planPush = useCallback(({ moved = [], dates = {}, links = [] }) => {
    if (!ganttOn || !hasDeps || !startFieldId) return { updates: [], error: null };
    const dateOf = (it, id) => (it && it.fields && it.fields[id] ? String(it.fields[id]).slice(0, 10) : '');
    const now = new Map();
    allItems.forEach((it, k) => now.set(k, { start: dateOf(it, startFieldId), end: dateOf(it, 'duedate'), frozen: isDone(it) }));
    Object.keys(dates).forEach((k) => {
      const was = now.get(k);
      const d = dates[k] || {};
      if (!was) return;
      now.set(k, { ...was, start: d.start !== undefined ? (d.start || '') : was.start, end: d.due !== undefined ? (d.due || '') : was.end });
    });
    const items = [];
    allItems.forEach((it) => items.push(it));
    const edges = buildDependencyEdges(items, depRules);
    const known = new Set(edges.map((e) => e.linkId));
    links.forEach((l) => { if (!known.has(l.linkId)) edges.push(l); });
    return pushLater(now, edges, { moved, links });
  }, [ganttOn, hasDeps, startFieldId, allItems, depRules]);
  const pushDependents = useCallback(async (change) => {
    const { updates, error } = planPush(change);
    if (error) {
      setSaveErrText('Nothing that depends on this was moved: its links go round in a loop, so there is no date that would satisfy them all.');
      return;
    }
    let done = 0;
    for (const u of updates) {
      const it = allItems.get(u.key);
      if (!it) continue;
      const patch = {};
      if (u.start) patch[startFieldId] = u.start;
      if (u.end) patch.duedate = u.end;
      // eslint-disable-next-line no-await-in-loop
      if (Object.keys(patch).length && await saveField(it, patch)) done += 1;
    }
    if (done) setFillNote(`${done === 1 ? '1 dependent work item' : `${done} dependent work items`} moved later to keep their links`);
  }, [planPush, allItems, startFieldId, saveField]);

  // MANY ROWS PATCHED IN ONE PASS. key → fields. patchLoaded copies every
  // folder's map once per key; a move of three hundred work items would
  // copy thirty thousand entries three hundred times.
  const patchMany = useCallback((m) => {
    if (!m || !m.size) return;
    setBuilt((prev) => (prev || []).map((p) => {
      let byKey = null;
      m.forEach((fields, key) => {
        if (!fields || !p.byKey.has(key)) return;
        if (!byKey) byKey = new Map(p.byKey);
        const it = byKey.get(key);
        byKey.set(key, { ...it, fields: { ...it.fields, ...fields } });
      });
      if (!byKey) return p;
      return { ...p, byKey, roots: p.roots.map((r) => (m.has(r.key) ? byKey.get(r.key) : r)) };
    }));
    setRefItems((prev) => {
      let next = null;
      m.forEach((fields, key) => {
        if (!fields || !prev.has(key)) return;
        if (!next) next = new Map(prev);
        const it = next.get(key);
        next.set(key, { ...it, fields: { ...it.fields, ...fields } });
      });
      return next || prev;
    });
  }, []);

  // ---- the Rescheduler ----------------------------------------------------------
  // A BAR DRAGGED WHOLE TAKES THE WORK UNDER IT ALONG — on a grid whose admin
  // switched it on, for the work types they named, for site admins and the
  // groups they named (./reschedule.js has the rules, ../../server/
  // reschedule.js the walk and the writes). Off, or for anyone else, a drag
  // is exactly what it always was.
  //
  // IT MUST LOOK INSTANT. The server walks the hierarchy and saves every work
  // item through the normal save — on a live site that is seconds for a big
  // tree — so the page does not wait for it: the moment the hand lets go,
  // the bar AND every loaded bar under it jump to their new dates (from the
  // plan read when the bar was hovered), each greyed while its save is in
  // flight. The server's own plan arrives with its first line and corrects
  // the guess; a save refused puts that one bar back; a refusal of the whole
  // move puts everything back and says why.
  const canReschedule = !!(cfg.rescheduleOn && startFieldId && configKey === 'structure' && page && page.slug
    && api && typeof api.gridsReschedule === 'function' && mayReschedule(cfg, userGroups, isAdminUser(userGroups)));
  const rescheduleRule = useCallback((it) => (canReschedule ? rescheduleRuleFor(cfg, rescheduleTypeOf(it)) : null), [canReschedule, cfg]);
  // The folder a row sits in, off its path (f:<id>/…, innermost wins) — the
  // server walks that folder's extenders.
  const folderIdOf = (row) => {
    let id = '';
    String((row && row.path) || '').split('/').forEach((s) => { if (s.startsWith('f:')) id = s.slice(2); });
    return id;
  };
  // What a whole move of each bar would take along, read once per bar on
  // hover and kept for half a minute. key → { at, promise, plan, error }.
  const planCache = useRef(new Map());
  const [planTick, setPlanTick] = useState(0);
  const loadReschedulePlan = useCallback((row, { fresh = false } = {}) => {
    const it = row && row.it;
    if (!it || !rescheduleRule(it)) return null;
    const key = it.key;
    const hit = planCache.current.get(key);
    if (hit && !fresh && Date.now() - hit.at < 30000) return hit.promise;
    const promise = api.gridsReschedulePlan(page.slug, { key, startFieldId, folderId: folderIdOf(row) })
      .then((plan) => { const e = planCache.current.get(key); if (e && e.promise === promise) { e.plan = plan; e.error = null; setPlanTick((t) => t + 1); } return plan; })
      .catch((err) => {
        const e = planCache.current.get(key);
        if (e && e.promise === promise) {
          e.error = (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'The work under it could not be read';
          e.at = 0;
          setPlanTick((t) => t + 1);
        }
        return null;
      });
    planCache.current.set(key, { at: Date.now(), promise, plan: hit && hit.plan, error: null });
    return promise;
  }, [api, page, startFieldId, rescheduleRule]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rescheduleInfo = useCallback((key) => planCache.current.get(key) || null, [planTick]);
  // Rows this page is writing itself: their broadcasts are its own echo, not
  // news from elsewhere, and are not read back (see onChanged below).
  const quietKeys = useRef(new Set());
  const [rsJob, setRsJob] = useState(null);
  const [rsReportOpen, setRsReportOpen] = useState(false);
  // THE CONFIRMATION SHOWS FOR FOUR SECONDS and goes. A move with refusals,
  // or one that failed, stays until it is dismissed — that needs reading —
  // and so does one whose report the reader has opened.
  useEffect(() => {
    if (!rsJob || rsJob.status !== 'done' || (rsJob.failed || []).length || rsReportOpen) return undefined;
    const t = setTimeout(() => setRsJob((cur) => (cur === rsJob ? null : cur)), 4000);
    return () => clearTimeout(t);
  }, [rsJob, rsReportOpen]);
  const runReschedule = useCallback(async (row, from, to, days) => {
    const it = row.it;
    const key = it.key;
    const dateOf = (x, id) => (x && x.fields && x.fields[id] ? String(x.fields[id]).slice(0, 10) : '');
    const held = () => allItemsRef.current;
    const before = new Map();       // key → the fields as they were, to put back
    const remember = (k) => {
      const cur = held().get(k);
      if (!cur) return null;
      if (!before.has(k)) before.set(k, { [startFieldId]: cur.fields[startFieldId] == null ? null : cur.fields[startFieldId], duedate: cur.fields.duedate == null ? null : cur.fields.duedate });
      return cur;
    };
    const patchFor = (d) => { const p = {}; if (d.start) p[startFieldId] = d.start; if (d.due) p.duedate = d.due; return p; };
    // THE GUESS, at once: the bar, and every loaded bar the plan read on hover
    // moves. A plan still on its way (a quick flick of a bar never hovered)
    // is used the moment it lands — unless the server's own has spoken first.
    const optimistic = new Map();
    const touched = new Set();
    let settled = false;
    const guessFrom = (plan) => {
      if (settled || !plan || !Array.isArray(plan.items)) return;
      const add = new Map();
      plan.items.forEach((x) => {
        if (!x.move || optimistic.has(x.key)) return;
        const cur = remember(x.key);
        if (!cur) return;
        const p = patchFor(shiftDates({ start: dateOf(cur, startFieldId), due: dateOf(cur, 'duedate') }, days));
        if (Object.keys(p).length) add.set(x.key, p);
      });
      if (!add.size) return;
      add.forEach((p, k) => { optimistic.set(k, p); touched.add(k); quietKeys.current.add(k); });
      patchMany(add);
      setSavingKeys((prev) => { const n = new Set(prev); add.forEach((_, k) => n.add(k)); return n; });
      setRsJob((j) => (j && j.status === 'running' && j.total == null ? { ...j, total: plan.counts ? plan.counts.moving : add.size } : j));
    };
    remember(key);
    optimistic.set(key, patchFor(to));
    touched.add(key);
    quietKeys.current.add(key);
    patchMany(new Map([[key, patchFor(to)]]));
    setSaveErrText(null);
    setSavingKeys((prev) => new Set(prev).add(key));
    setRsJob({ key, days, status: 'running', done: 0, total: null });
    const entry = planCache.current.get(key);
    if (entry && entry.plan) guessFrom(entry.plan);
    else if (entry && entry.promise) entry.promise.then(guessFrom).catch(() => {});
    const unsave = (keys) => setSavingKeys((prev) => { const n = new Set(prev); keys.forEach((k) => n.delete(k)); return n; });
    // A LINE PER SAVED WORK ITEM, BUT NOT A REPAINT PER LINE: three hundred
    // saves would repaint the whole grid three hundred times. The saved keys
    // are gathered and let go a few times a second.
    const landed = [];
    let lastProgress = null;
    let flushTimer = 0;
    const flushProgress = () => {
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = 0; }
      if (landed.length) unsave(landed.splice(0));
      if (lastProgress) { const pr = lastProgress; lastProgress = null; setRsJob((j) => (j ? { ...j, done: pr.done, total: pr.total } : j)); }
    };
    let planned = null;
    try {
      const result = await api.gridsReschedule(page.slug, { key, startFieldId, folderId: folderIdOf(row), from, to }, {
        onPlan: (p) => {
          settled = true;
          planned = p;
          // THE SERVER'S PLAN, which wins: what it moves goes where it says;
          // a guess it does not move goes back to where it was.
          const moving = new Map((p.moving || []).map((m) => [m.key, m]));
          const fix = new Map();
          moving.forEach((m, k) => {
            if (!remember(k)) return;
            fix.set(k, patchFor(m));
            touched.add(k);
            quietKeys.current.add(k);
          });
          optimistic.forEach((_, k) => { if (k !== key && !moving.has(k)) fix.set(k, before.get(k)); });
          patchMany(fix);
          setSavingKeys((prev) => {
            const n = new Set(prev);
            n.delete(key);                      // the bar's own save has landed
            optimistic.forEach((_, k) => { if (k !== key && !moving.has(k)) n.delete(k); });
            moving.forEach((_, k) => { if (held().has(k)) n.add(k); });
            return n;
          });
          setRsJob((j) => (j ? { ...j, total: (p.moving || []).length, staying: p.staying || [], levels: p.levels, perLevel: p.perLevel, counts: p.counts } : j));
        },
        onProgress: (pr) => {
          landed.push(pr.key);
          lastProgress = pr;
          if (!flushTimer) flushTimer = setTimeout(flushProgress, 120);
        },
      });
      flushProgress();
      // A save the server refused is still on its old dates.
      if ((result.failed || []).length) {
        const back = new Map();
        result.failed.forEach((f) => { if (before.has(f.key)) back.set(f.key, before.get(f.key)); });
        patchMany(back);
      }
      setRsJob((j) => ({ ...(j || {}), key, days, status: 'done', moved: result.moved || [], failed: result.failed || [], levels: result.levels, counts: result.counts, seconds: result.seconds, done: (result.moved || []).length + (result.failed || []).length }));
      if ((result.failed || []).length) setRsReportOpen(true);
      // WHAT DEPENDS ON THE MOVED WORK FOLLOWS IT, later — as after any drag.
      if (hasDeps) {
        const movedKeys = [key, ...(result.moved || [])];
        const dates = {};
        const byKey = new Map(((planned && planned.moving) || []).map((m) => [m.key, m]));
        movedKeys.forEach((k) => {
          const m = k === key ? to : byKey.get(k);
          if (m) dates[k] = { start: m.start || undefined, due: m.due || undefined };
        });
        await pushDependents({ moved: movedKeys, dates });
      }
    } catch (e) {
      // REFUSED (or cut off): back to what the page knew, and anything the
      // server may already have written is read back to be sure.
      settled = true;
      patchMany(before);
      touched.forEach((k) => quietKeys.current.delete(k));
      const msg = (e && e.message) || 'failed';
      setSaveErrText(`${key} — ${msg}`);
      setRsJob((j) => ({ ...(j || {}), key, days, status: 'failed', error: msg }));
      if (planned) {
        const q = liveQueue.current;
        touched.forEach((k) => q.changed.add(k));
        if (!q.timer) q.timer = setTimeout(() => { q.timer = null; flushLive(); }, 300);
      }
    } finally {
      settled = true;
      if (flushTimer) { clearTimeout(flushTimer); flushTimer = 0; }
      unsave([...touched]);
      planCache.current.clear();
      setPlanTick((t) => t + 1);
      // The echoes of these saves arrive for a moment after the last one.
      setTimeout(() => touched.forEach((k) => quietKeys.current.delete(k)), 4000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, page, startFieldId, patchMany, hasDeps, pushDependents]);

  // ---- dragging a bar --------------------------------------------------------
  // The grid does the pixels and the days; only the page knows WHICH FIELDS a
  // start and a due date live in on this site, so it is handed { start, due }
  // and turns that into a write.
  //
  // A structure with no start-date column has nowhere to put a start, and
  // silently dropping half a drag would be worse than refusing it — the bar
  // would spring back with no explanation. So it says so.
  const onTimelineEdit = useCallback((row, dates, how = {}) => {
    const it = row && row.it;
    if (!it || !dates) return;
    // A WHOLE BAR, of a work type that carries the work under it: the
    // Rescheduler moves the bar and that work together. One end dragged (a
    // change of duration) is an ordinary edit, below.
    if (how && how.handle === 'move' && dates.start !== undefined && dates.due !== undefined && rescheduleRule(it)) {
      const dateOf = (x, id) => (x && x.fields && x.fields[id] ? String(x.fields[id]).slice(0, 10) : '');
      const from = { start: dateOf(it, startFieldId), due: dateOf(it, 'duedate') };
      const days = wholeMove(from, dates);
      if (days) { runReschedule(row, from, { start: dates.start, due: dates.due }, days); return; }
    }
    const patch = {};
    if (dates.due !== undefined) patch.duedate = dates.due;
    if (dates.start !== undefined) {
      if (!startFieldId) {
        setSaveErrText('The start date cannot be changed from the timeline: this structure has no start-date column, so there is no field to write. Add one in Page settings.');
        return;
      }
      patch[startFieldId] = dates.start;
    }
    if (!Object.keys(patch).length) return;
    // The bar first, on its own terms — it is what the hand did — and what
    // depends on it only once that write has landed.
    (async () => {
      if (await saveField(it, patch)) await pushDependents({ moved: [it.key], dates: { [it.key]: dates } });
    })();
  }, [saveField, startFieldId, pushDependents, rescheduleRule, runReschedule]);

  // ---- drawing a dependency --------------------------------------------------
  // The grid does the gesture (a dot dragged from one bar's end to another's)
  // and names the kind; the page writes the link the grid's rule for that
  // kind holds, through the product's own link route — the same request the
  // folders Gantt makes. Both rows are patched with it straight away so the
  // arrow is there at once, without re-reading either; the server's own
  // broadcast brings the canonical rows a moment later.
  const addLoadedLink = useCallback((key, entry) => {
    setBuilt((prev) => (prev || []).map((p) => {
      if (!p.byKey.has(key)) return p;
      const byKey = new Map(p.byKey);
      const it = byKey.get(key);
      const links = Array.isArray(it.fields && it.fields.issuelinks) ? it.fields.issuelinks : [];
      if (links.some((l) => l && l.id === entry.id)) return p;
      const next = { ...it, fields: { ...it.fields, issuelinks: [...links, entry] } };
      byKey.set(key, next);
      const roots = p.roots.map((r) => (r.key === key ? next : r));
      return { ...p, byKey, roots };
    }));
  }, []);
  const onLink = useCallback(async ({ type, predKey, succKey }) => {
    const rule = hasDeps ? depRules[type] : null;
    if (!rule || !rule.linkTypeName || !predKey || !succKey || predKey === succKey) return;
    if (!allItems.has(predKey) || !allItems.has(succKey)) return;
    // One dependency per pair, as in the folders Gantt.
    const items = [];
    allItems.forEach((it) => items.push(it));
    if (buildDependencyEdges(items, depRules).some((e) => (e.predKey === predKey && e.succKey === succKey) || (e.predKey === succKey && e.succKey === predKey))) return;
    const payload = buildLinkPayload(rule, predKey, succKey);
    setSaveErrText(null);
    // A LINK THAT CLOSES A LOOP IS NOT MADE. Weighed before anything is
    // written: if the work behind this link already depends on it, no dates
    // could ever satisfy both, and a link that can never be met is not one.
    const probe = { type, predKey, succKey, lagDays: rule.lagDays || 0, linkId: `probe-${predKey}-${succKey}` };
    if (planPush({ links: [probe] }).error) {
      setSaveErrText(`${predKey} → ${succKey} — the link was not made: ${succKey} already leads, through its own links, back to ${predKey}, so this would make a loop.`);
      return;
    }
    let made;
    try {
      made = await api.linkIssue(payload);
    } catch (e) {
      const msg = (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'failed';
      setSaveErrText(`${predKey} → ${succKey} — the link was not made: ${msg}`);
      return;
    }
    const linkId = made && made.linkId != null ? String(made.linkId) : `new-${predKey}-${succKey}`;
    const linkType = { id: rule.linkTypeId, name: rule.linkTypeName };
    const inKey = payload.inwardIssue.key;
    const outKey = payload.outwardIssue.key;
    // Each end names the OTHER, on the side it is on — the shape the rows
    // already hold, and the one the arrows are found from.
    addLoadedLink(inKey, { id: linkId, type: linkType, outwardIssue: { key: outKey } });
    addLoadedLink(outKey, { id: linkId, type: linkType, inwardIssue: { key: inKey } });
    await pushDependents({ links: [{ type, predKey, succKey, lagDays: rule.lagDays || 0, linkId }] });
  }, [hasDeps, depRules, allItems, api, addLoadedLink, planPush, pushDependents]);

  // A work item created by the "+" belongs in the tree under the row that
  // made it, and it belongs there NOW — not after a reload that would cost
  // the reader their place and their open folders. It is added to every
  // folder whose branch already holds the parent.
  //
  // `before` runs in the same synchronous step as the insert, so whatever
  // the caller is showing in the meantime (the typed row) leaves in the
  // same paint the real row arrives in — no flash of both, no gap.
  const onCreated = useCallback(async (parent, made, before = null) => {
    setSaveErrText(null);
    try {
      const fresh = await api.searchIssues(`key = ${made.key}`, LOAD_FIELDS, 1, null, 0, true);
      const issue = ((fresh && fresh.issues) || [])[0];
      if (!issue) { if (before) before(); setReloadTick((t) => t + 1); return; }
      if (before) before();
      setBuilt((prev) => (prev || []).map((pt) => {
        if (!pt.byKey.has(parent.key)) return pt;
        const byKey = new Map(pt.byKey);
        byKey.set(issue.key, issue);
        const kidsOf = new Map(pt.kidsOf);
        const kids = (kidsOf.get(parent.key) || []).slice();
        if (!kids.includes(issue.key)) kids.push(issue.key);
        kidsOf.set(parent.key, kids);
        return { ...pt, byKey, kidsOf };
      }));
      // The parent opens so the new row is in view. The item's own screen
      // does NOT open: the reader is building a structure, not touring it.
      setCollapsed((prev) => {
        const n = new Set(prev);
        [...n].forEach((p) => { if (String(p).endsWith(`/${parent.key}`)) n.delete(p); });
        return n;
      });
    } catch {
      // The item exists; only our copy of the tree is behind. Say so by
      // reloading rather than by pretending nothing happened.
      setReloadTick((t) => t + 1);
    }
  }, [api, LOAD_FIELDS]);

  // ---- the work item's own screen changes the tree ------------------------------
  // A value edited on the screen is patched into the row. A DELETE takes the
  // row out of the tree — it used to reload the whole structure, minutes,
  // with the reader's open folders gone. A CREATE from inside the screen (a
  // child, a linked item) re-reads that one parent's children and adds what
  // is new; nothing else is touched.
  const removeLoaded = useCallback((key) => {
    setBuilt((prev) => (prev || []).map((p) => withoutItem(p, key)));
    setPopupItem(null);
  }, []);
  const refreshChildren = useCallback(async (key) => {
    try {
      const [fresh, byParent] = await Promise.all([
        api.searchIssues(`key = ${key}`, LOAD_FIELDS, 1, null, 0, true),
        fetchAll(`parent = ${key}`, LOAD_FIELDS, null, MAX_CAP),
      ]);
      const parent = ((fresh && fresh.issues) || [])[0];
      if (!parent) return;
      // Linked children, by every link rule of every folder holding this
      // parent — at any level, because a child created from the screen is
      // one level down whatever its parent's own depth.
      const rules = [];
      (cfg.folders || []).forEach((f) => (f.expand.links || []).forEach((l) => rules.push({ ...l, levelFrom: 1, levelTo: 99 })));
      const linked = linkedChildKeys(parent, rules, 1);
      const have = new Set(byParent.map((c) => c.key));
      const missing = linked.filter((k) => !have.has(k) && !allItems.has(k));
      const extra = missing.length ? await fetchAll(`key in (${missing.join(', ')})`, LOAD_FIELDS, null, MAX_CAP) : [];
      const kids = [...byParent, ...extra, ...linked.filter((k) => allItems.has(k)).map((k) => allItems.get(k))];
      setBuilt((prev) => (prev || []).map((pt) => {
        if (!pt.byKey.has(key)) return pt;
        const byKey = new Map(pt.byKey);
        byKey.set(key, { ...byKey.get(key), fields: { ...byKey.get(key).fields, ...parent.fields } });
        const kidsOf = new Map(pt.kidsOf);
        const list = (kidsOf.get(key) || []).slice();
        kids.forEach((c) => {
          if (!c || !c.key || c.key === key) return;
          if (!byKey.has(c.key)) byKey.set(c.key, c);
          if (!list.includes(c.key)) list.push(c.key);
        });
        kidsOf.set(key, list);
        return { ...pt, byKey, kidsOf };
      }));
    } catch {
      // The tree is behind the server and cannot say by how much: reload.
      setReloadTick((t) => t + 1);
    }
  }, [api, LOAD_FIELDS, fetchAll, cfg.folders, allItems]);
  // A parent changed on the screen: the row MOVES (movedItem), with what is
  // under it, and the pop-up stays open on it. A folder that holds the new
  // parent but not the row (it was not shown here before) reads that
  // parent's children again, which hangs it there.
  const moveLoaded = useCallback((key, newParentKey) => {
    const needsRead = !!newParentKey && (built || []).some((p) => p.byKey.has(newParentKey) && !p.byKey.has(key));
    setBuilt((prev) => (prev || []).map((p) => movedItem(p, key, newParentKey)));
    // An item also read beside the tree (refItems) wears its new Parent
    // field there too, as patchLoaded did for it.
    setRefItems((prev) => {
      if (!prev.has(key)) return prev;
      const m = new Map(prev);
      const it = m.get(key);
      m.set(key, { ...it, fields: { ...it.fields, parent: newParentKey ? { key: newParentKey } : null } });
      return m;
    });
    if (needsRead) refreshChildren(newParentKey);
  }, [built, refreshChildren]);
  // The row the screen reports on, as the page holds it now (read at the
  // moment of the report, so the callback itself never changes).
  const heldOf = useRef(() => null);
  heldOf.current = (k) => allItems.get(k) || refItems.get(k) || null;
  const onPopupChanged = useCallback((key, fields, how) => {
    // The screen reports a parent change alone, as { parent: { key } | null }.
    if (fields && Object.keys(fields).length === 1 && 'parent' in fields) {
      moveLoaded(key, (fields.parent && fields.parent.key) || null);
      return;
    }
    if (fields) {
      // Only what could change the row (patchForGrid, ./compact.js): opening
      // an item with attachments used to rebuild the whole tree for them.
      const patch = patchForGrid(heldOf.current(key), fields, new Set([...loadedFieldIds, ...fetchedExtra.current]));
      if (patch) patchLoaded(key, patch);
      return;
    }
    if (how === 'deleted') { removeLoaded(key); return; }
    refreshChildren(key);
  }, [patchLoaded, removeLoaded, refreshChildren, moveLoaded, loadedFieldIds]);

  // ---- other people's changes ----------------------------------------------------
  // A GRID IS A SNAPSHOT, AND THIS IS HOW IT STOPS BEING A STALE ONE. Every
  // write to a work item — a field, a transition, a comment, a bulk edit, a
  // script — is broadcast to every open tab as 'issues-changed' (the
  // server's broadcaster, modules/database/broadcast.js). The grid listens
  // and does three small things, none of which is a reload:
  //
  //   updated   a row THIS tree holds → its fields are read again, in the
  //             fields this page loads, and patched into the row in place
  //   deleted   a row this tree holds → taken out of the tree
  //   created   a child whose parent this tree holds → read and hung
  //             under that parent, in every folder that holds it
  //
  // A work item that is new to the tree but has no parent here (a new
  // project, a new root of a folder's insert query) is NOT chased: only a
  // reload can tell whether a folder's query would insert it, and that is
  // what ↻ is for.
  //
  // EVENTS ARE BATCHED. A bulk edit or a clone run fires hundreds of events
  // in a second; they are gathered for a moment and read back in one
  // query per thousand keys — never one request per event. A row this
  // reader is saving right now is left to their own save. The payload's
  // own field subset is NOT trusted for the patch: it carries the writer's
  // shape, not this page's, and a select or a user written in one shape
  // and read in another is exactly the bug editShape.js exists for — so
  // the row is read back the way the page reads everything else.
  const liveQueue = useRef({ changed: new Set(), created: new Map(), deleted: new Set(), timer: null });
  const savingKeysRef = useRef(savingKeys);
  useEffect(() => { savingKeysRef.current = savingKeys; }, [savingKeys]);
  const flushLive = useCallback(async () => {
    const q = liveQueue.current;
    const changed = new Set([...q.changed].filter((k) => !savingKeysRef.current.has(k)));
    const created = new Map(q.created);
    const deleted = [...q.deleted];
    q.changed.clear(); q.created.clear(); q.deleted.clear();
    if (deleted.length) {
      setBuilt((prev) => (prev || []).map((p) => deleted.reduce((acc, k) => withoutItem(acc, k), p)));
      setPopupItem((cur) => (cur && deleted.includes(cur.key) ? null : cur));
    }
    if (!changed.size && !created.size) {
      if (deleted.length) setFillNote(`${deleted.length === 1 ? 'A row was' : `${deleted.length} rows were`} deleted elsewhere`);
      return;
    }
    try {
      const held = allItemsRef.current;
      const fresh = new Map();
      const fetchKeys = async (keys) => {
        const want = [...new Set(keys)].filter((k) => k && !fresh.has(k));
        if (!want.length) return;
        const pool = makePool(FILTER_PARALLEL);
        const got = await Promise.all(chunkKeys(want, FILTER_CHUNK).map((chunk) => pool(() => fetchAll(
          `key in (${chunk.join(', ')})`, LOAD_FIELDS, null, MAX_CAP,
        ))));
        got.forEach((rows) => (rows || []).forEach((it) => { if (it && it.key) fresh.set(it.key, it); }));
      };
      // Every folder's link rules, at every level — a child made from the
      // screen is one level down whatever its parent's own depth.
      const rulesOf = (folder) => (folder.expand.links || []).map((l) => ({ ...l, levelFrom: 1, levelTo: 99 }));
      const allRules = [];
      cfg.folders.forEach((f) => rulesOf(f).forEach((r) => allRules.push(r)));
      const partnerOf = (it) => {
        // The held rows this item's own links name, from either side.
        const out = [];
        ((it && it.fields && it.fields.issuelinks) || []).forEach((l) => {
          const other = l && (l.inwardIssue || l.outwardIssue);
          if (other && other.key && held.has(other.key)) out.push(other.key);
        });
        return out;
      };

      // Round 1: the rows that changed and the rows that were created.
      await fetchKeys([...changed, ...created.keys()]);
      // A created item's parent is the payload's, else its own Parent field.
      // With neither — a LINKED child — the held rows its links name are
      // the candidates, and they are read again so their links say so.
      const parentOf = new Map();
      const partners = new Set();
      created.forEach((pk, key) => {
        const it = fresh.get(key);
        const parent = pk || (it && it.fields && it.fields.parent && it.fields.parent.key) || null;
        if (parent && held.has(parent)) parentOf.set(key, parent);
        else if (it) partnerOf(it).forEach((p) => partners.add(p));
      });
      partners.forEach((p) => changed.add(p));
      // Round 2: the partners (and anything a changed row's links now name
      // that the tree does not hold).
      await fetchKeys([...partners]);
      // A ROW THAT READS BACK AS IT WAS IS LEFT ALONE. A comment, a field no
      // column shows, a save that wrote what was there: patching it would
      // make a new object, and a new object makes the page work out its
      // whole tree again — half a second, frozen, at forty thousand rows,
      // in every open copy of the grid. Only a row whose re-read could
      // show differently goes on (unchangedForGrid, ./compact.js).
      const wanted = LOAD_FIELDS.split(',');
      [...changed].forEach((key) => {
        if (held.has(key) && fresh.has(key) && unchangedForGrid(held.get(key), fresh.get(key), wanted, cfg.loadMode === 'server')) changed.delete(key);
      });
      const linkedNew = new Set();
      changed.forEach((key) => {
        const it = fresh.get(key);
        if (!it) return;
        linkedChildKeys(it, allRules, 1).forEach((ck) => { if (ck !== key && !held.has(ck)) linkedNew.add(ck); });
      });
      await fetchKeys([...linkedNew]);

      let added = 0;
      // Nothing touched → the SAME tree back, so nothing downstream reruns.
      setBuilt((prev) => { const next = (prev || []).map((pt) => {
        let byKey = pt.byKey; let kidsOf = pt.kidsOf; let roots = pt.roots; let touched = false;
        const own = () => { if (!touched) { byKey = new Map(pt.byKey); kidsOf = new Map(pt.kidsOf); touched = true; } };
        const hang = (pk, ck) => {
          if (!byKey.has(pk) || pk === ck) return;
          own();
          if (!byKey.has(ck)) { const it = fresh.get(ck); if (!it) return; byKey.set(ck, it); added += 1; }
          const kids = (kidsOf.get(pk) || []).slice();
          if (!kids.includes(ck)) { kids.push(ck); kidsOf.set(pk, kids); }
        };
        const rules = rulesOf(pt.folder);
        // Changed rows: the fields this page loads, read again, in place —
        // and any child their links now name, hung under them.
        changed.forEach((key) => {
          const it = fresh.get(key);
          if (!it || !pt.byKey.has(key)) return;
          own();
          const was = byKey.get(key);
          const next = { ...was, fields: { ...was.fields, ...it.fields } };
          byKey.set(key, next);
          roots = roots.map((r) => (r.key === key ? next : r));
          if (rules.length) linkedChildKeys(next, rules, 1).forEach((ck) => hang(key, ck));
        });
        // Created rows: under their parent, wherever this folder holds it.
        parentOf.forEach((pk, key) => { if (!pt.byKey.has(key)) hang(pk, key); });
        return touched ? { ...pt, byKey, kidsOf, roots } : pt;
      }); return prev && next.every((pt, i) => pt === prev[i]) ? prev : next; });
      const parts = [];
      const nChanged = [...changed].filter((k) => held.has(k) && fresh.has(k)).length;
      if (nChanged) parts.push(`${nChanged === 1 ? '1 row' : `${nChanged} rows`} updated`);
      const nAdded = created.size + linkedNew.size;
      if (nAdded) parts.push(`${nAdded === 1 ? '1 row' : `${nAdded} rows`} added`);
      if (deleted.length) parts.push(`${deleted.length === 1 ? '1 row' : `${deleted.length} rows`} deleted`);
      // A created item with no parent here and no held row in its links is a
      // new TOP-LEVEL item: only a reload can say whether a folder's insert
      // query would take it, so the note says so.
      const orphans = [...created.keys()].filter((k) => !parentOf.has(k) && fresh.has(k) && !partnerOf(fresh.get(k)).length);
      // ASK THE FOLDERS whether they would take it: each folder's own insert
      // queries, over just these keys — a handful of tiny queries, so a
      // top-level task made in some other project does not rebuild the page.
      // One that would (a new Project on the WIP) brings a quiet reload.
      let belongs = false;
      if (orphans.length) {
        const inKeys = `key in (${orphans.slice(0, FILTER_CHUNK).join(', ')})`;
        const asks = [];
        cfg.folders.forEach((f) => (f.inserts || []).forEach((q) => {
          const zql = (q.zql || '').trim();
          if (zql) asks.push(andZql(inKeys, andZql(zql, f.filterZql)));
        }));
        const pool = makePool(FILTER_PARALLEL);
        const got = await Promise.all(asks.map((zql) => pool(() => fetchAll(zql, 'key', null, orphans.length).catch(() => []))));
        belongs = got.some((rows) => (rows || []).length > 0);
      }
      if (belongs) {
        setFillNote('A new work item was made elsewhere — bringing it in…');
        if (quietTimer.current) clearTimeout(quietTimer.current);
        // A moment's wait, so a project created with its plan underneath
        // arrives as one reload, not one per work item.
        quietTimer.current = setTimeout(() => {
          quietTimer.current = null;
          quietNext.current = true;
          setReloadTick((t) => t + 1);
        }, 1500);
      } else if (parts.length) setFillNote(`${parts.join(', ')} elsewhere${orphans.length ? ' — a new top-level item needs ↻' : ''}`);
    } catch {
      // The tree is behind the server and cannot say by how much — but a
      // failed catch-up must never take the page down. Say so quietly.
      setFillNote('Some changes made elsewhere could not be read — press ↻ to reload');
    }
  }, [fetchAll, LOAD_FIELDS, cfg.folders, cfg.loadMode]);
  // The note clears itself; a change made elsewhere is news, not a banner.
  useEffect(() => {
    if (!fillNote || !/elsewhere|reload|moved later/.test(fillNote)) return undefined;
    const t = setTimeout(() => setFillNote((cur) => (cur === fillNote ? '' : cur)), 4000);
    return () => clearTimeout(t);
  }, [fillNote]);
  const allItemsRef = useRef(allItems);
  useEffect(() => { allItemsRef.current = allItems; }, [allItems]);
  useEffect(() => {
    if (!built || soloKey) return undefined;
    const onChanged = (payload) => {
      const { event, issueKey, parentKey } = payload || {};
      if (!issueKey) return;
      const have = allItemsRef.current;
      const q = liveQueue.current;
      if (event === 'jira:issue_deleted') {
        if (!have.has(issueKey)) return;
        q.deleted.add(issueKey);
      } else if (event === 'jira:issue_created') {
        // A child made by the Parent field names its parent here; one made
        // by a LINK (the grid's own "+" on most sites) names nothing, and
        // is read to find out. Either way it is queued.
        if (have.has(issueKey)) return;
        // Made while a quiet reload is building (or about to): the new tree
        // may have been read before it existed, so it waits for that tree.
        if (quietBusy.current || quietTimer.current || quietNext.current) {
          createdDuringQuiet.current.set(issueKey, parentKey || null);
          return;
        }
        q.created.set(issueKey, parentKey && have.has(parentKey) ? parentKey : null);
      } else {
        // The echo of this page's own Rescheduler saves: the row already
        // holds what was written. Read back only if the move fails.
        if (quietKeys.current.has(issueKey)) return;
        if (!have.has(issueKey)) return;
        q.changed.add(issueKey);
      }
      if (!q.timer) q.timer = setTimeout(() => { q.timer = null; flushLive(); }, 700);
    };
    socket.on('issues-changed', onChanged);
    // The socket is shared with the rest of the shell and is normally up
    // already; if this page is the first to want it, it asks once.
    try { if (!socket.connected && localStorage.getItem('token')) socket.connect(); } catch { /* storage may be blocked */ }
    return () => {
      socket.off('issues-changed', onChanged);
      const q = liveQueue.current;
      if (q.timer) { clearTimeout(q.timer); q.timer = null; }
    };
  }, [built, soloKey, flushLive]);
  // The quiet reload has landed: what was created while it built and is not
  // in the new tree goes through the ordinary catch-up, against the new tree.
  useEffect(() => {
    if (!built || quietBusy.current || quietTimer.current || quietNext.current) return;
    const waiting = createdDuringQuiet.current;
    if (!waiting.size) return;
    const q = liveQueue.current;
    waiting.forEach((pk, k) => { if (!allItemsRef.current.has(k)) q.created.set(k, pk); });
    waiting.clear();
    if (q.created.size && !q.timer) q.timer = setTimeout(() => { q.timer = null; flushLive(); }, 700);
  }, [built, loadGen, flushLive]);
  useEffect(() => () => { if (quietTimer.current) clearTimeout(quietTimer.current); }, []);

  // ---- selecting rows, and cloning them -----------------------------------------
  // A tick left of the key; the count and a way into Zebra Search above the
  // grid; and, for the rows whose type the admin opened up, the sheep. The
  // cloning itself is ./clone.js — what is here is the wiring: the dialog,
  // the job running behind it, and the new work landing in the tree beside
  // what it was cloned from.
  const [selected, setSelected] = useState(() => new Set());
  const onSelect = useCallback((next) => setSelected(next), []);
  // WHO MAY CLONE is the admin's call too (Page settings → Cloning): a reader
  // outside the named groups gets no sheep and no Clone button. Admins
  // always may. (userGroups is read at the top of the component.)
  const canClone = mayClone(cfg, userGroups, isAdminUser(userGroups));
  const cloneable = useCallback((it) => canClone && isCloneable(cfg, it), [cfg, canClone]);
  const selectedKeys = useMemo(() => [...selected].filter((k) => allItems.has(k)), [selected, allItems]);
  const cloneRoots = useMemo(() => selectedKeys.filter((k) => cloneable(allItems.get(k))), [selectedKeys, allItems, cloneable]);
  // Every child list of every folder, in one map — the tree the dialog
  // draws and the plan walks.
  const treeAll = useMemo(() => {
    const m = new Map();
    (built || []).forEach((p) => p.kidsOf.forEach((kids, pk) => {
      const cur = m.get(pk) || [];
      kids.forEach((k) => { if (!cur.includes(k)) cur.push(k); });
      m.set(pk, cur);
    }));
    return m;
  }, [built]);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneJob, setCloneJob] = useState(null);
  // The create screens, once per (project, work type) per session.
  const metaCache = useRef(new Map());
  const metaOf = useCallback(async (projectKey, typeId, typeName) => {
    let id = typeId;
    if (!id && typeName && workTypes && typeof workTypes.get === 'function') {
      const t = workTypes.get(String(typeName).toLowerCase());
      id = t && t.id;
    }
    const k = `${projectKey}:${id}`;
    if (!metaCache.current.has(k)) metaCache.current.set(k, api.getCreateMeta(projectKey, id).catch((e) => { metaCache.current.delete(k); throw e; }));
    return metaCache.current.get(k);
  }, [api, workTypes]);
  // EVERY FIELD OF THE ORIGINALS, read once per key and remembered. The page
  // holds each work item with only the fields its columns asked for; a
  // clone made from that would carry a summary and two dates and nothing a
  // validator wants. This answers the whole work item, two hundred keys a
  // query, for the dialog's counts and for the copies alike.
  const fullCache = useRef(new Map());
  const loadFull = useCallback(async (keys) => {
    const want = [...new Set((keys || []).filter((k) => k && !fullCache.current.has(k)))];
    if (want.length) {
      const pool = makePool(FILTER_PARALLEL);
      const got = await Promise.all(chunkKeys(want, 200).map((chunk) => pool(() => fetchAll(`key in (${chunk.join(', ')})`, '*all', null, MAX_CAP))));
      got.forEach((rows) => (rows || []).forEach((it) => { if (it && it.key) fullCache.current.set(it.key, it); }));
    }
    const out = new Map();
    (keys || []).forEach((k) => { if (fullCache.current.has(k)) out.set(k, fullCache.current.get(k)); });
    return out;
  }, [fetchAll]);
  // WHERE A COPY MAY GO: the projects the site has, the work items whose type
  // may hold the copies by the target project's creation tree — SEARCHED
  // ACROSS STRIDE, not only this grid's rows (a clone is made in Stride; the
  // grid is only where it was started) — and whether a project offers a work
  // type at all. `onGrid` says which candidates this grid has loaded.
  const loadProjects = useCallback(() => api.getProjects().then((list) => (Array.isArray(list) ? list : []).filter((p) => p && p.key).map((p) => ({ key: p.key, name: p.name || p.key }))), [api]);
  const loadParents = useCallback((childTypes, projectKey, text = '') => searchParents(api, { projectKey, childTypeNames: childTypes, text }), [api]);
  const onGrid = useCallback((key) => allItems.has(key), [allItems]);
  const typeIn = useCallback(async (projectKey, typeName) => {
    const types = await loadTypes(api, projectKey);
    const want = String(typeName || '').trim().toLowerCase();
    return (types || []).find((t) => t && String(t.name || '').trim().toLowerCase() === want) || null;
  }, [api]);
  const startClone = useCallback(async ({ plan, clear, copies, edits = null, project = null, placement = 'same', parentKey = null }) => {
    if (!plan || !plan.length) return;
    setCloneJob({ status: 'running', done: 0, total: plan.length * copies, createdCount: 0, planSize: plan.length, copies, plan, project, parentKey });
    const result = await runClone({
      api, plan, byKey: allItems, copies, clear, edits, project, typeIn, metaOf, loadFull,
      onProgress: (p) => setCloneJob((j) => (j ? { ...j, done: p.done, total: p.total, createdCount: p.created } : j)),
    });
    // THE NEW WORK LANDS IN THE TREE where it was hung: a cloned root sits
    // after its original under every parent that holds it — or under the
    // parent the reader picked instead; a cloned child hangs off its
    // cloned parent.
    try {
      const keys = result.created.map((c) => c.key);
      const fresh = keys.length ? await fetchAll(`key in (${keys.join(', ')})`, LOAD_FIELDS, null, MAX_CAP) : [];
      const freshBy = new Map(fresh.map((it) => [it.key, it]));
      const parentOf = new Map(plan.map((n) => [n.key, n.parentKey]));
      setBuilt((prev) => (prev || []).map((pt) => {
        const byKey = new Map(pt.byKey);
        const kidsOf = new Map(pt.kidsOf);
        let roots = pt.roots;
        let touched = false;
        for (let copy = 1; copy <= copies; copy += 1) {
          const map = new Map(result.created.filter((c) => c.copy === copy).map((c) => [c.from, c.key]));
          map.forEach((newKey, from) => {
            const it = freshBy.get(newKey);
            if (!it) return;
            const pk = parentOf.get(from);
            if (pk) {
              const newParent = map.get(pk);
              if (!newParent || !byKey.has(newParent)) return;
              byKey.set(newKey, it);
              const list = (kidsOf.get(newParent) || []).slice();
              if (!list.includes(newKey)) list.push(newKey);
              kidsOf.set(newParent, list);
              touched = true;
            } else if (placement === 'new' && parentKey) {
              if (!byKey.has(parentKey)) return;
              byKey.set(newKey, it);
              const list = (kidsOf.get(parentKey) || []).slice();
              if (!list.includes(newKey)) list.push(newKey);
              kidsOf.set(parentKey, list);
              touched = true;
            } else if (pt.byKey.has(from)) {
              byKey.set(newKey, it);
              if (roots.some((r) => r.key === from)) {
                const i = roots.findIndex((r) => r.key === from);
                roots = [...roots.slice(0, i + 1), it, ...roots.slice(i + 1)];
              }
              kidsOf.forEach((kids, parent) => {
                const i = kids.indexOf(from);
                if (i >= 0 && !kids.includes(newKey)) kidsOf.set(parent, [...kids.slice(0, i + 1), newKey, ...kids.slice(i + 1)]);
              });
              touched = true;
            }
          });
        }
        return touched ? { ...pt, byKey, kidsOf, roots } : pt;
      }));
    } catch { /* the report still says what was made; a reload shows it in place */ }
    setCloneJob({ status: 'done', created: result.created, errors: result.errors, planSize: plan.length, copies, plan, project, parentKey });
    setSelected(new Set());
  }, [api, allItems, metaOf, fetchAll, LOAD_FIELDS, loadFull, typeIn]);
  const filterClones = useCallback((keys) => {
    setCloneFilter(new Set(keys));
    setCloneOpen(false);
  }, []);
  const searchHref = useMemo(() => (selectedKeys.length ? `/issues?jql=${encodeURIComponent(`key in (${selectedKeys.join(', ')})`)}` : ''), [selectedKeys]);

  // ---- creating in place --------------------------------------------------------
  // A "+" has picked a type. The row the new work item will become is drawn
  // in the tree under its parent — see draftRow in Grid.jsx — and the
  // workflow's Create validators are asked in the background. Enter makes
  // the item on the spot when they want nothing beyond a summary; when they
  // do want more, the product's full create screen opens instead.
  const [newRow, setNewRow] = useState(null);
  const [createScreen, setCreateScreen] = useState(null);
  const startDraft = useCallback((parent, option, options, parentPath) => {
    // The parent opens, so the row has somewhere to be.
    setCollapsed((prev) => { if (!prev.has(parentPath)) return prev; const n = new Set(prev); n.delete(parentPath); return n; });
    const needs = preflightCreate({ parent, option, summary: '' }).catch(() => []);
    setNewRow({ parent, option, options, parentPath, needs, busy: false, error: null });
  }, []);
  const cancelDraft = useCallback(() => setNewRow(null), []);
  const submitDraft = useCallback(async (summary) => {
    const d = newRow;
    if (!d || d.busy) return;
    setNewRow({ ...d, busy: true, error: null, summary });
    const blockers = await d.needs;
    const wanted = [...new Set((blockers || []).flatMap((b) => b.fields || []))];
    // THE SUMMARY ALREADY TYPED GOES WITH IT. A validator that wants more
    // than a summary opens the full create screen — which used to open
    // blank, so the reader typed the summary twice.
    if (wanted.length) {
      setNewRow(null);
      setCreateScreen({ parent: d.parent, option: d.option, options: d.options, summary });
      return;
    }
    try {
      const made = await createChild(api, { parent: d.parent, option: d.option, summary });
      // THE TYPED ROW STAYS UNTIL THE REAL ONE IS IN PLACE. Dropping it the
      // moment the create returned left a gap of a second or two while the
      // new work item was read back — the row vanished, then reappeared
      // with its key. Now the summary sits where it was typed and the key
      // arrives beside it.
      await onCreated(d.parent, made, () => setNewRow(null));
      setNewRow(null);
    } catch (e) {
      if (validatorRefusal(e)) { setNewRow(null); setCreateScreen({ parent: d.parent, option: d.option, options: d.options, summary }); return; }
      const msg = (e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'refused';
      setNewRow((cur) => (cur ? { ...cur, busy: false, error: String(msg) } : cur));
    }
  }, [newRow, api, onCreated]);
  const draftForGrid = useMemo(() => (newRow ? {
    parentPath: newRow.parentPath, parentKey: newRow.parent.key,
    type: { id: newRow.option.issueTypeId, name: newRow.option.issueTypeName, iconUrl: newRow.option.iconUrl },
    busy: newRow.busy, error: newRow.error, summary: newRow.summary || '', onSubmit: submitDraft, onCancel: cancelDraft,
  } : null), [newRow, submitDraft, cancelDraft]);

  // ---- exports ----------------------------------------------------------------
  const dashRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  // The slice of the chart on screen, at the grain the reader chose — what
  // the PDF draws, so the paper says what the screen says.
  const chartViewRef = useRef(null);
  const onChartView = useCallback((v) => { chartViewRef.current = v; }, []);
  const pageName = (page && page.name) || 'Structure';
  const filtersText = useMemo(() => {
    const p = activeButtons.map((b) => b.caption || b.zql);
    filterDefs.forEach((f) => { const s = sel[f.id]; if (s && s.size) p.push(`${f.label}: ${[...s].join(', ')}`); });
    if (zqlApplied.trim()) p.push(`ZQL ${zqlApplied.trim()}`);
    if (doneMode !== 'all') p.push((DONE_MODES.find((d) => d.id === doneMode) || {}).label);
    if (cloneFilter) p.push(`my clones (${cloneFilter.size})`);
    if (search.trim()) p.push(`search “${search.trim()}”`);
    return `${p.join(' · ') || 'no filters'} — ${rows.filter((r) => r.kind === 'item').length.toLocaleString()} rows on screen`;
  }, [activeButtons, filterDefs, sel, zqlApplied, doneMode, search, rows, cloneFilter]);
  // Both exports are built from the ROWS, not from the DOM — the grid is
  // windowed, so a DOM-read export would carry the thirty rows that happen
  // to be on screen and quietly call it the structure.
  const exportPdf = async () => {
    if (exportingPdf || !built) return;
    setExportingPdf(true);
    try {
      await toPdf({
        rows, cols, ctx, name: pageName, pageName, filtersText,
        logoDataUri: lookAndFeel.logoDataUri,
        // THE CHART GOES WITH IT, at the interval on screen and from the
        // same left-hand edge. A structure exported with the timeline on
        // and no bars is a list of dates, which is the thing the Gantt
        // exists to stop people reading.
        timeline: ganttOn, spanOfRow,
        grain: (chartViewRef.current && chartViewRef.current.grain) || ganttGrain || 'day',
        chartView: chartViewRef.current,
      });
    } catch (e) { window.alert(`The PDF could not be built: ${(e && e.message) || e}`); }
    finally { setExportingPdf(false); }
  };
  const [exportingXls, setExportingXls] = useState(false);
  const exportExcel = async () => {
    if (exportingXls || !built) return;
    setExportingXls(true);
    try { await toExcel({ rows, cols, ctx, name: pageName, filtersText }); }
    catch (e) { window.alert(`The workbook could not be built: ${(e && e.message) || e}`); }
    finally { setExportingXls(false); }
  };

  // ---- the toolbar ------------------------------------------------------------
  // NO ROW COUNTER. "73 rows on screen" cost a full scan of the row array on
  // every expand, filter and edit — on a structure of twenty-eight thousand
  // that is real work, every click, to answer a question nobody was asking.
  // The count that MEANS something (how much work the structure holds) is a
  // Set's size and is free.
  const distinct = allItems.size;
  const chev = (down) => <svg width="10" height="10" viewBox="0 0 10 10" style={{ flex: '0 0 auto', transform: down ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><path d="M3 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  const iconBtn = (on, title, onClick, glyph) => (
    <button onClick={onClick} title={title} style={{
      ...S.exportBtn, padding: '4px 9px', fontSize: 13, lineHeight: 1,
      background: on ? ui.primary : ui.card, color: on ? ui.primaryFg : ui.muted, borderColor: on ? ui.primary : ui.border,
    }}>{glyph}</button>
  );

  // THE PAGE NAME SITS ON THE TOOLBAR'S OWN LINE. It had a line of its own
  // above the toolbar, which on a structure page is a line of rows nobody
  // can see — the data is the page, and every millimetre above the grid is
  // taken from it.
  const toolbar = (
    <div style={{ position: 'sticky', top: 0, zIndex: 20, background: ui.card, border: `1px solid ${ui.border}`, borderRadius: ui.radius, boxShadow: ui.shadowSm, padding: '6px 12px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* A HOST'S OWN BUTTON before the name (toolbarStart) — a page that draws
            this grid inside itself puts its "open on its own" there. */}
        {toolbarStart}
        {!full ? <h1 style={{ ...S.h1, marginRight: 6 }}>{pageName}</h1> : null}
        {dirty && adminMode && !full ? <span style={{ fontSize: 12, fontWeight: 700, color: ui.amber }}>● unsaved settings (previewing)</span> : null}
        {adminMode ? iconBtn(showRules, showRules ? 'Hide the generators' : 'Show the generators this structure is built from — click any of them to change it', () => setShowRules((v) => !v), '📌') : null}
        {cfg.gantt.on ? iconBtn(ganttOn, ganttOn ? 'Hide the timeline' : 'Show the timeline', () => setGanttOn((v) => !v), '📊') : null}
        {iconBtn(full, full ? 'Leave full screen (Esc)' : 'Full screen — the structure takes the whole page; the navigation stays', toggleFull, '⛶')}
        <button onClick={() => setFiltersOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>
          {chev(filtersOpen)} Filter / Group
        </button>
        {/* NOT A NATIVE <select>. One was the only thing on this toolbar the
            operating system drew rather than the page: a grey chrome box
            whose open list is the OS's own, in the OS's own font, with a
            hard blue highlight that belongs to no theme this app has. Next
            to a row of pills it looked like a control from another decade —
            and it is three mutually exclusive choices, which is exactly what
            a segmented control is FOR. The options are now visible without
            opening anything, which is the other half of the win. */}
        <span style={{
          display: 'inline-flex', border: `1px solid ${ui.border}`, borderRadius: ui.radiusFull,
          overflow: 'hidden', background: ui.card,
        }}>
          {DONE_MODES.map((d, i) => (
            <button key={d.id} onClick={() => setDoneMode(d.id)} title={`${d.label} — finished work`}
              style={{
                fontSize: 12, fontWeight: 700, padding: '5px 11px', cursor: 'pointer',
                border: 'none', borderLeft: i ? `1px solid ${ui.border}` : 'none',
                background: doneMode === d.id ? `${ui.primary}1f` : 'transparent',
                color: doneMode === d.id ? ui.fg : ui.muted,
                whiteSpace: 'nowrap', fontFamily: FONT,
              }}>{d.label}</button>
          ))}
        </span>
        {/* THE QUICK BUTTONS: a pill each, and a CLUSTER (buttons sharing a
            `group`) as one segmented control drawn like the Done control
            beside it — one on at a time; the one that is on, pressed again,
            lets go. A button with iconType wears that work type's own icon
            (the site's, so a new icon in Settings shows here too). Each
            carries data-button-id for a host page. */}
        {buttonParts(cfg.buttons).map((part) => {
          const press = (b) => setButtonsOn((prev) => pressButton(prev, cfg.buttons, b.id));
          const face = (b) => (
            <>
              {b.iconType ? <TypeIcon issue={iconSampleOf(b.iconType)} types={workTypes} ui={ui} size={14} /> : null}
              {b.caption || b.zql}
            </>
          );
          if (!part.group) {
            const b = part.button;
            return (
              <button key={b.id} data-button-id={b.id} aria-pressed={buttonsOn.has(b.id)} onClick={() => press(b)}
                title={b.zql} style={{ ...S.pill(buttonsOn.has(b.id)), display: 'inline-flex', alignItems: 'center', gap: 6 }}>{face(b)}</button>
            );
          }
          return (
            <span key={`g:${part.group}`} role="group" aria-label={part.group} data-button-group={part.group} style={{
              display: 'inline-flex', border: `1px solid ${ui.border}`, borderRadius: ui.radiusFull,
              overflow: 'hidden', background: ui.card,
            }}>
              {part.buttons.map((b, i) => (
                <button key={b.id} data-button-id={b.id} aria-pressed={buttonsOn.has(b.id)} onClick={() => press(b)} title={b.zql}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12, fontWeight: 700, padding: '5px 11px', cursor: 'pointer',
                    border: 'none', borderLeft: i ? `1px solid ${ui.border}` : 'none',
                    background: buttonsOn.has(b.id) ? `${ui.primary}1f` : 'transparent',
                    color: buttonsOn.has(b.id) ? ui.fg : ui.muted,
                    whiteSpace: 'nowrap', fontFamily: FONT,
                  }}>{face(b)}</button>
              ))}
            </span>
          );
        })}
        {/* NO KEYBOARD AMBUSH — see the same guard on every filter input in
            Stride. */}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Contains text…" autoFocus={false}
          style={{ ...S.input, padding: '5px 10px', fontSize: 13, flex: '1 1 150px', minWidth: 130, maxWidth: 240 }} />
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...S.tiny, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            {!built ? `${progress.phase || 'building'}… ${progress.rows.toLocaleString()}`
              : <><span style={{ width: 8, height: 8, borderRadius: 8, background: ui.green, display: 'inline-block' }} /><strong style={{ color: ui.fg }}>{distinct.toLocaleString()}</strong> work items{lines.all.size ? <span style={{ color: ui.muted }}>&nbsp;· {lines.all.size.toLocaleString()} table lines</span> : null}</>}
          </span>
          {/* THE FILTER SAYS IT IS WORKING. The rows stay on screen while
              the server answers; without this line the button looked like
              it had done nothing at all. */}
          {built && matches.pending ? <span style={{ ...S.tiny, color: ui.amber, fontWeight: 700 }}>applying the filter…</span> : null}
          {fillNote ? <span style={{ ...S.tiny, color: ui.amber, fontWeight: 700 }}>{fillNote}</span> : null}
          {/* THE CLONE RUN KEEPS COUNT HERE while its dialog is shut, and
              the report waits here when it is done. */}
          {cloneJob && !cloneOpen ? (
            <button onClick={() => setCloneOpen(true)} style={{ ...S.exportBtn, display: 'inline-flex', alignItems: 'center', gap: 5, color: cloneJob.status === 'running' ? ui.amber : ui.fg }}>
              <DnaIcon size={15} title="" />
              {cloneJob.status === 'running' ? `Cloning ${cloneJob.done}/${cloneJob.total}…` : `Cloned ${cloneJob.created.length}${cloneJob.errors.length ? ` · ${cloneJob.errors.length} ⚠` : ''} — report`}
            </button>
          ) : null}
          {/* THE RESCHEDULER KEEPS COUNT HERE while a move saves, and says
              what happened when it has — click for the report. */}
          <RescheduleChip ui={ui} S={S} job={rsJob} onOpen={() => setRsReportOpen((o) => !o)}
            onDismiss={() => { setRsJob(null); setRsReportOpen(false); }} />
          {/* BOTH, ALWAYS. One button that swapped between Expand all and
              Collapse all hid whichever one the reader wanted next — after
              opening one folder by hand the tree is neither, and the button
              could only offer one of the two. Each is dimmed when there is
              nothing for it to do, and still pressable. */}
          <button onClick={expandAll} disabled={!paths.length} title="Open every row in the tree"
            style={{ ...S.exportBtn, opacity: paths.length && !allOpen ? 1 : 0.5 }}>Expand all</button>
          <button onClick={collapseAll} disabled={!paths.length} title="Shut every row in the tree"
            style={{ ...S.exportBtn, opacity: paths.length && !allShut ? 1 : 0.5 }}>Collapse all</button>
          {anyFilter && <button onClick={clearFilters} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>Clear</button>}
          <button onClick={exportExcel} disabled={!built || exportingXls} style={{ ...S.exportBtn, opacity: built && !exportingXls ? 1 : 0.5 }} title="Every row on screen, in these columns">{exportingXls ? 'Building…' : 'Excel'}</button>
          <button onClick={exportPdf} disabled={!built || exportingPdf} style={{ ...S.exportBtn, opacity: built && !exportingPdf ? 1 : 0.5 }}>{exportingPdf ? 'Building…' : 'PDF'}</button>
          <button onClick={() => { setKeySets({}); setReloadTick((t) => t + 1); }} disabled={!built} style={{ ...S.exportBtn, opacity: built ? 1 : 0.5 }} title="Reload from the server">↻</button>
        </span>
      </div>

      {/* THE QUERY BOX GROWS WITH THE QUERY. It opens with Filter / Group
          and folds away with it, so it costs no row of the structure while
          nobody is typing. The mode switch, the box, the clear and the Apply
          are drawn as ONE pill, so the eye reads "a query" rather than four
          widgets; and the pill is as wide as the query in it — see
          queryWidth — up to the width of the toolbar, never narrower than it
          began. Inside the pill the autocomplete's own chrome (its border,
          its badge, its focus ring) is switched off: the pill is the chrome. */}
      {/* ZQL HAS THE HELPING EDITOR: the query coloured as it is typed, the
          fields, operators and real values offered at the caret, a problem
          named where it sits with its one-click fix, the engine's own count
          before anything is applied, recent queries, and a saved filter
          pulled in to be edited. See ./ZqlBox. */}
      {filtersOpen && searchMode === 'zql' ? (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, flexWrap: narrow ? 'wrap' : 'nowrap' }}>
        <span style={{ paddingTop: 6 }}>{queryModeSwitch}</span>
        <div style={{ flex: '1 1 auto', minWidth: narrow ? '100%' : 320, maxWidth: 1200 }}>
          <ZqlBox value={zqlBox} onChange={setZqlBox} onApply={(q) => setZqlApplied(q)}
            onClear={() => { setZqlBox(''); setZqlApplied(''); }} applied={zqlApplied} disabled={!built}
            api={api} ui={ui} S={S} recentKey={`grids-zql-recent-${storeKey}`}
            placeholder="ZQL — e.g. status = Open AND duedate < endOfMonth()"
            countWords={(n) => `work item${n === 1 ? '' : 's'} match site-wide — Apply keeps the ones in this structure`}
            hint={zqlError ? null : (zqlApplied && zqlApplied === zqlBox.trim() ? '✓ applied to the structure' : (zqlApplied ? `In force: ${zqlApplied} — Enter or Apply runs the edit` : undefined))} />
          {zqlError ? <div style={{ ...S.tiny, color: ui.red, fontWeight: 700, marginTop: 4 }}>⚠ {zqlError}</div> : null}
        </div>
      </div>
      ) : null}
      {filtersOpen && searchMode === 'text' ? (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <style>{`
          .grids-query .jql-autocomplete-wrapper { display: block; min-width: 0; }
          .grids-query .jql-autocomplete-badge { display: none; }
          .grids-query .jql-autocomplete-wrapper input[type="text"] {
            border: none; background: transparent; box-shadow: none; outline: none;
            padding: 6px 6px; font-size: 13px; color: inherit; width: 100%; min-width: 0;
            font-family: "SF Mono", Menlo, Consolas, "Fira Mono", ui-monospace, monospace;
          }
          .grids-query .jql-autocomplete-wrapper input[type="text"]:focus { box-shadow: none; }
        `}</style>
        <div className="grids-query" style={{
          display: 'flex', alignItems: 'center', gap: 4,
          flex: `0 1 ${queryWidth}px`, minWidth: narrow ? '100%' : 320, maxWidth: '100%', boxSizing: 'border-box',
          border: `1px solid ${zqlError ? ui.red : (zqlApplied ? ui.primary : ui.border)}`,
          borderRadius: 999, background: ui.card, padding: 3, boxShadow: ui.shadowXs,
          transition: 'flex-basis .15s ease, border-color .15s ease',
        }}>
          {/* One box, two jobs. ZQL gets the full suggestion list the rest of
              Stride gives; Text takes plain words and asks the server for
              summary-or-description while matching every loaded value here. */}
          {queryModeSwitch}
          <span style={{ flex: '1 1 auto', minWidth: 0 }}>
            {(
              <input value={zqlBox} onChange={(e) => setZqlBox(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setZqlApplied(zqlBox); }}
                placeholder="Words to find…"
                style={{ ...S.input, border: 'none', background: 'transparent', boxShadow: 'none', padding: '6px 6px', fontSize: 13 }} />
            )}
          </span>
          {zqlBox || zqlApplied ? (
            <button onClick={() => { setZqlBox(''); setZqlApplied(''); }} title="Clear the query"
              style={{ flex: '0 0 auto', border: 'none', background: 'transparent', cursor: 'pointer', color: ui.muted, fontSize: 16, lineHeight: 1, padding: '2px 6px', fontFamily: FONT }}>×</button>
          ) : null}
          <button onClick={() => setZqlApplied(zqlBox)} disabled={!built || !zqlBox.trim()}
            title={searchMode === 'zql' ? 'Run the query over the structure (Enter)' : 'Search (Enter)'}
            style={{
              flex: '0 0 auto', border: 'none', cursor: built && zqlBox.trim() ? 'pointer' : 'default', fontFamily: FONT,
              fontSize: 12, fontWeight: 800, padding: '7px 14px', borderRadius: 999, lineHeight: 1,
              background: built && zqlBox.trim() ? ui.primary : ui.surface, color: built && zqlBox.trim() ? ui.primaryFg : ui.muted,
              opacity: built ? 1 : 0.6, transition: 'background .12s',
            }}>Apply</button>
        </div>
        {zqlError ? <span style={{ ...S.tiny, color: ui.red, fontWeight: 700 }}>⚠ {zqlError}</span> : null}
        {zqlApplied && !zqlError && zqlApplied === zqlBox ? <span style={{ ...S.tiny, color: ui.green, fontWeight: 700 }}>✓ applied</span> : null}
      </div>
      ) : null}
      {/* A query that is still in force while the panel is shut is said in
          one line, so a filtered page never looks like an unfiltered one. */}
      {!filtersOpen && zqlApplied ? (
        <div style={{ ...S.tiny, marginTop: 4 }}>
          {searchMode === 'text' ? 'Searching' : 'ZQL'} <strong style={{ color: ui.fg }}>{zqlApplied}</strong>
          <button onClick={() => { setZqlBox(''); setZqlApplied(''); }} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: '0 6px' }}>clear</button>
        </div>
      ) : null}
      {filtersOpen && searchMode === 'text' && zqlApplied ? (
        <div style={{ ...S.tiny, marginTop: 4 }}>
          Searching <strong style={{ color: ui.fg }}>{zqlApplied}</strong> in summary and description site-wide,
          and in every field this page loaded. Comments are not searchable from here — this site’s ZQL has no comment field.
        </div>
      ) : null}

      {filtersOpen && (
        <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {filterDefs.map((f) => (
            <div key={f.id} style={{ flex: narrow ? '1 1 100%' : '0 1 auto', minWidth: narrow ? '100%' : 150 }}>
              <label style={S.label}>{f.label}</label>
              <MultiPick value={[...(sel[f.id] || [])]} options={filterOptions[f.id] || []}
                onChange={(vals) => setSel((s) => ({ ...s, [f.id]: new Set(vals) }))} placeholder="All" ui={ui} S={S} width={narrow ? '100%' : 200} />
            </div>
          ))}
          <div style={{ ...S.tiny, flex: '1 1 100%' }}>
            The filters run over the whole structure — a folder or a project stays on screen while anything under it matches.
          </div>
        </div>
      )}
    </div>
  );

  // ---- admin's scope note -----------------------------------------------------
  const [scopeOpen, setScopeOpen] = useState(false);
  const code = (s) => <code style={{ background: ui.surface, padding: '1px 5px', borderRadius: 4, fontSize: 11.5 }}>{s}</code>;
  const scopeInfo = adminMode && (
    <div>
      <button onClick={() => setScopeOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>
        {chev(scopeOpen)} Data scope — what this page loaded and ran
      </button>
      {scopeOpen && (
        <div style={{ ...S.tiny, marginTop: 8, lineHeight: 1.7 }}>
          <div>• Load: {cfg.loadMode === 'server'
            ? <>on the server, in <strong style={{ color: ui.fg }}>one</strong> request — {loadInfo && loadInfo.wireBytes != null ? <><strong style={{ color: ui.fg }}>{(loadInfo.wireBytes / 1048576).toFixed(1)} MB</strong> on the wire</> : 'loading…'}{loadInfo && loadInfo.requests != null ? <>, {loadInfo.requests.toLocaleString()} queries between the API and its database</> : null}{loadInfo ? `, in ${(loadInfo.seconds || 0).toFixed(1)} s` : ''}</>
            : cfg.loadMode === 'universe'
            ? <>one broad load of {code(cfg.universeZql.trim() || cfg.filterZql || '(nothing)')} — {loadInfo ? `${loadInfo.universe.toLocaleString()} work items` : 'loading…'}</>
            : <>level by level, {cfg.walkChunk} parents a query, {cfg.walkParallel} at a time — only what the tree reaches is loaded{loadInfo ? `, in ${(loadInfo.seconds || 0).toFixed(1)} s` : '…'}</>}</div>
          {/* WHAT IT COST, IN ROUND TRIPS. This page is network-bound — the
              wall clock is queries × latency and very little else — so the
              one number worth printing is how many were sent, beside how
              many the shared cache answered without one. "It feels slow"
              has nowhere to go without them; with them the next move
              (`walkChunk`, `walkParallel`, or the other loader) is a
              measurement rather than a guess. */}
          {loadInfo && loadInfo.queries != null && cfg.loadMode !== 'server' ? (
            <div>• Cost: <strong style={{ color: ui.fg }}>{loadInfo.queries.toLocaleString()}</strong> queries
              {loadInfo.saved ? <> — and <strong style={{ color: ui.green }}>{loadInfo.saved.toLocaleString()}</strong> asks the folders answered from each other rather than sending</> : null}
              {loadInfo.seconds ? <>, in {loadInfo.seconds.toFixed(1)} s</> : null}
              . The folders walk together and share one pool of {cfg.walkParallel}, so this is the whole page&rsquo;s traffic, not one folder&rsquo;s.
            </div>
          ) : null}
          {loadInfo && loadInfo.excluded != null ? <div>• Filter: kept a row whenever anything under it matches — {loadInfo.excluded.toLocaleString()} work items are rejected by {code(cfg.filterZql)} site-wide, and one of them still shows when it is the route to a match</div> : null}
          {loadInfo && loadInfo.filterNote ? <div style={{ color: ui.amber, fontWeight: 700 }}>• Filter: {loadInfo.filterNote}</div> : null}
          {loadInfo && loadInfo.capped ? <div style={{ color: ui.red, fontWeight: 700 }}>• The load hit the {cfg.maxRows.toLocaleString()}-row cap — the tree below it is INCOMPLETE. Raise the cap or narrow the query.</div> : null}
          {(loadInfo ? loadInfo.folders : []).map((f) => (
            <div key={f.name}>• {f.name}: {f.roots.toLocaleString()} inserted, {f.items.toLocaleString()} in the branch, {f.depth} level(s) deep</div>
          ))}
          <div>• Columns: {allCols.map((c) => <span key={c.id}>{c.label}{c.field ? <> = {fieldIds[c.id] ? code(fieldIds[c.id]) : <strong style={{ color: ui.red }}>“{c.field}” NOT FOUND</strong>}</> : ''}; </span>)}</div>
          {missingFields.length ? <div style={{ color: ui.red, fontWeight: 700 }}>• This site has no field named: {missingFields.join(', ')} — those cells and groupings will be blank.</div> : null}
          <div>• Filtered: {filtersText}.</div>
        </div>
      )}
    </div>
  );

  // ---- the stock check ---------------------------------------------------------
  // Every Stock check column (./stock.js), netted over EVERY loaded work item
  // and line — not the rows on screen: what the shelf can give is a fact about
  // all the work, and a filter must not make a part look safe. Per column:
  // the rows of its work types that need something (B), each with what A
  // looked its part up by and what A found (still asking → "…").
  const stockNets = useMemo(() => {
    const out = new Map();
    const stockCols = (cfg.columns || []).filter((c) => c.kind === 'stock' && c.a && c.b);
    if (!stockCols.length) return out;
    const base = { itemOf, fieldIds, columns: cfg.columns, lookupOf, now: new Date() };
    const cands = [];
    allItems.forEach((it) => cands.push(it));
    lines.all.forEach((l) => cands.push(l));
    stockCols.forEach((sc) => {
      const A = cfg.columns.find((c) => c.id === sc.a); const B = cfg.columns.find((c) => c.id === sc.b);
      if (!A || !B) return;
      const test = typeTest(sc);
      // THE PARTS THE GRID MAKES: every work item's own value of A's field
      // (its Part Number) — a line of that part is made here, not taken from
      // stock (./stock.js), unless the column judges them anyway.
      const madeBy = new Map();
      if (!sc.judgeMade && A.kind === 'lookup' && fieldIds[A.id]) {
        allItems.forEach((it, k) => { const v = textsOf(it, fieldIds[A.id])[0]; if (v && !madeBy.has(String(v).toLowerCase())) madeBy.set(String(v).toLowerCase(), k); });
      }
      const entries = [];
      cands.forEach((x) => {
        if (test && !test(x)) return;
        const row = x.line ? { line: x, key: x.key } : { it: x, key: x.key };
        const need = cellOf(B, row, base).num;
        if (!(need > 0)) return;
        let part = x.key; let avail = 0; let pending = false; let error = null;
        if (A.kind === 'lookup' && A.lookup) {
          part = textsOf(x, fieldIds[A.id])[0] || '';
          if (!part) return;
          const look = lookupOf(A, part);
          if (look === undefined) pending = true;
          else if (look && look.error) error = look.error;
          else avail = look ? (figureOf(look.value) || 0) : 0;
        } else {
          avail = cellOf(A, row, base).num || 0;
        }
        const maker = madeBy.get(String(part).toLowerCase());
        entries.push({
          key: x.key, owner: x.line ? x.line.of : null, part, need, avail, pending, error, done: isDone(x),
          made: maker && maker !== x.key ? maker : null,
          due: x.line ? x.line.due : String((x.fields && x.fields.duedate) || '').slice(0, 10),
        });
      });
      out.set(sc.id, netStock(entries));
    });
    return out;
  }, [cfg.columns, allItems, lines, itemOf, fieldIds, lookupOf]);
  const stockOf = useCallback((col) => stockNets.get(col.id) || null, [stockNets]);

  // ---- the grid ---------------------------------------------------------------
  const ctx = useMemo(() => ({
    itemOf, refOf: itemOf, fieldIds, now: new Date(), columns: cfg.columns, resolve: resolveName, lookupOf, stockOf,
    linesOf: (k) => linesByOwner.get(k) || NO_LINES,
    todayIso: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
  }), [itemOf, fieldIds, cfg.columns, resolveName, lookupOf, stockOf, linesByOwner]);

  // The data IS the page, so the grid takes the height that is left rather
  // than a fixed box with the window empty beneath it. Full screen takes
  // the lot; otherwise it is the window less the chrome above it, measured
  // rather than guessed.
  const chromeRef = useRef(null);
  const [chromeH, setChromeH] = useState(180);
  useEffect(() => {
    const el = chromeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setChromeH(el.getBoundingClientRect().height));
    ro.observe(el);
    setChromeH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [adminMode, filtersOpen, built]);
  // THE GRID MUST END ABOVE THE FOLD, or its horizontal scrollbar — the
  // only way to reach the columns off the right-hand edge — sits below the
  // viewport where nobody can see it. Guessing at the chrome's height got
  // this wrong by 16px and hid the scrollbar completely, so the grid's own
  // top is MEASURED and the height is whatever is left.
  const gridBoxRef = useRef(null);
  const [gridTop, setGridTop] = useState(240);
  useEffect(() => {
    const measure = () => {
      const el = gridBoxRef.current;
      if (el) setGridTop(el.getBoundingClientRect().top + (typeof window !== 'undefined' ? window.scrollY : 0));
    };
    measure();
    const t1 = setTimeout(measure, 60);      // after the chrome has laid out
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && chromeRef.current) ro.observe(chromeRef.current);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t1); if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [adminMode, filtersOpen, built, full, ganttOn, showRules, searchMode]);
  // 18px of air at the bottom: the scrollbar's own height plus a little, so
  // it is never flush against the edge of the window. A page that embeds the
  // structure below its own content (a custom page with charts on top) and
  // scrolls to it passes minGridHeight, so the rows still get a whole page
  // rather than whatever was left under that content; full screen ignores it.
  const gridHeight = Math.max(full ? 260 : Math.max(260, minGridHeight || 0), vh - gridTop - 18 - (ganttOn ? 34 : 0));

  // ---- the loader --------------------------------------------------------------
  // A structure this size takes real seconds to assemble, and a blank card
  // for those seconds reads as a broken page. The site's own logo, a bar
  // that tracks the load, and a line saying what is being done — the same
  // loader the KPI page uses, for the same reason.
  const pctLoaded = progress.rows ? Math.min(96, 14 + (progress.rows / Math.max(1, cfg.maxRows)) * 82) : 10;
  const loader = (
    <div style={{ ...S.card, marginBottom: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '34px 20px' }}>
      <style>{'@keyframes wip-breathe { 0%,100% { transform: scale(1); opacity: .85 } 50% { transform: scale(1.06); opacity: 1 } } @keyframes wip-sweep { 0% { left: -40% } 100% { left: 110% } }'}</style>
      <div style={{ position: 'relative', width: 260, height: 74, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 12, animation: 'wip-breathe 1.8s ease-in-out infinite' }}>
        {lookAndFeel.logoDataUri
          ? <img src={lookAndFeel.logoDataUri} alt="" style={{ maxWidth: 240, maxHeight: 64, objectFit: 'contain' }} />
          : <span style={{ fontSize: 22, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{pageName}</span>}
        <span style={{ position: 'absolute', top: 0, bottom: 0, width: '30%', background: `linear-gradient(90deg, transparent, ${ui.card}cc, transparent)`, animation: 'wip-sweep 1.4s linear infinite' }} />
      </div>
      <div style={{ width: 'min(520px, 100%)', height: 8, borderRadius: 8, background: ui.surface, border: `1px solid ${ui.border}`, overflow: 'hidden' }}>
        <div style={{ width: `${pctLoaded}%`, height: '100%', background: ui.blue, transition: 'width .4s' }} />
      </div>
      <div style={{ ...S.tiny, textAlign: 'center' }}>
        <div style={{ fontWeight: 700, color: ui.fg }}>
          Building the structure{progress.rows ? ` — ${progress.rows.toLocaleString()} work items so far` : ''}
        </div>
        <div>
          {progress.phase || 'starting'} — {cfg.loadMode === 'server'
            ? 'on the server, in one request'
            : cfg.loadMode === 'walk'
            ? 'level by level, only what the structure reaches'
            : 'one broad load'}; every folder, filter and sort after this is instant
        </div>
      </div>
    </div>
  );

  const body = (
    <>
      <div ref={chromeRef}>
        {adminMode && !full && (
          <SettingsPanel draft={draft} setDraft={setDraft} onSave={saveSettings} onRevert={revertSettings} dirty={dirty}
            saving={saving} error={saveErr} saved={savedFlag} ui={ui} S={S} testZql={testZql} fieldList={fieldList}
            history={history} onRollback={rollBack} loadInfo={scopeInfo} workTypes={workTypes}
            listGrids={listOtherGrids} cloningOf={cloningOfGrid} rescheduleOf={rescheduleOfGrid}
            canHostRescheduler={configKey === 'structure' && !!(api && typeof api.gridsReschedule === 'function')} hasStartColumn={!!startFieldId} />
        )}

        {toolbar}

        {/* WHAT IS TICKED, above the columns: how many, a way to see them
            in Zebra Search, and — for the rows the admin opened up — Clone. */}
        {selectedKeys.length ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '5px 12px', marginBottom: 6, border: `1px solid ${ui.primary}`, background: `${ui.primary}12`, borderRadius: ui.radiusSm, fontSize: 12.5 }}>
            <strong>{selectedKeys.length.toLocaleString()} work item{selectedKeys.length === 1 ? '' : 's'} selected</strong>
            <a href={searchHref} onClick={(e) => { e.preventDefault(); openTab(searchHref, `${selectedKeys.length} selected`); }}
              title="Open exactly these work items in Zebra Search" style={{ color: ui.blue, fontWeight: 700, textDecoration: 'none' }}>↗ Open in Zebra Search</a>
            <span style={S.tiny}>Shift-click or Shift+↓ ticks a run.</span>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              {cloneRoots.length ? (
                <button onClick={() => { setCloneJob(null); setCloneOpen(true); }} title={`Clone ${cloneRoots.length} of the selected work items — the ones whose type may be cloned`}
                  style={{ ...S.pill(true), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <DnaIcon size={18} title="" /> Clone {cloneRoots.length < selectedKeys.length ? `${cloneRoots.length} of ${selectedKeys.length}` : cloneRoots.length}
                </button>
              ) : (cfg.cloneTypes.length && canClone ? <span style={S.tiny}>none of these can be cloned ({cfg.cloneTypes.join(', ')} can)</span> : null)}
              <button onClick={() => setSelected(new Set())} style={S.exportBtn}>Clear selection</button>
            </span>
          </div>
        ) : null}

        {error && <div style={{ ...S.card, marginBottom: 12, color: ui.red, fontWeight: 700 }}>{error}</div>}
        {saveErrText ? <div style={{ ...S.tiny, color: ui.red, fontWeight: 700, marginBottom: 6 }}>{saveErrText}</div> : null}
      </div>

      {!built && !error ? loader : null}

      <div ref={(el) => { dashRef.current = el; gridBoxRef.current = el; }}>
        {built && (
          <Grid rows={rows} cols={cols} ctx={ctx} ui={ui} S={S} workTypes={workTypes} isDark={isDark}
            editable={cfg.editable} frozenIds={frozenIds} onFreeze={adminMode ? freezeColumn : null} height={gridHeight} fitRows={fitRows && !full} rowHeight={narrow ? ROW_H_TOUCH : ROW_H} narrow={narrow}
            onToggle={toggle} onOpenItem={setPopupItem} onEdit={saveField} savingKeys={savingKeys}
            api={api} onCreated={onCreated} onStatusChanged={patchLoaded}
            timeline={ganttOn} onTimelineEdit={onTimelineEdit} spanOfRow={spanOfRow} itemHref={itemHref}
            dependencies={cfg.gantt && cfg.gantt.dependencies} onLink={hasDeps ? onLink : null}
            dateWindow={dateWindow} onFieldsWanted={onFieldsWanted} onChartView={onChartView}
            selected={selected} onSelect={onSelect} cloneable={cfg.cloneTypes.length && canClone ? cloneable : null}
            onDraft={startDraft} draft={draftForGrid} headWrap={cfg.headWrap}
            reschedulable={canReschedule ? rescheduleRule : null} rescheduleInfo={canReschedule ? rescheduleInfo : null}
            onRescheduleIntent={canReschedule ? loadReschedulePlan : null}
            grain={ganttGrain} onGrain={setGanttGrain}
            storeKey={storeKey}
            restoreTop={restoreTop} onScrolled={onScrolled}
            adminMode={adminMode} allColumns={cfg.columns} fieldList={fieldList}
            onEditRule={adminMode ? setEditRule : undefined} onRemoveRule={adminMode ? onRemoveRule : undefined}
            onMoveFolder={adminMode ? moveFolder : null}
            onColumns={(fn) => setDraft((d0) => ({ ...d0, columns: fn(normalizeConfig(d0, defaults).columns) }))}
            emptyText={matches.pending ? 'Applying the filter…' : 'Nothing in the current filters.'} />
        )}
        {/* NO FOOTER STATS. The line under the grid repeated a number already
            in the toolbar and added two more that had to be computed to say
            it — and it sat below the fold on every screen it was meant for. */}
      </div>
      {createScreen ? (
        <CreateScreen item={createScreen.parent} option={createScreen.option} options={createScreen.options} api={api}
          summary={createScreen.summary || ''}
          onClose={() => setCreateScreen(null)}
          // The screen closes itself after a create — unless Create another
          // is ticked, when it stays open for the next one.
          onCreated={(parent, made) => onCreated(parent, made)} />
      ) : null}
      {cloneOpen && (cloneRoots.length || cloneJob) ? (
        <ClonePanel roots={cloneJob && cloneJob.plan ? cloneJob.plan.filter((n) => !n.parentKey).map((n) => n.key) : cloneRoots}
          byKey={allItems} kidsOf={treeAll} api={api} ui={ui} S={S} workTypes={workTypes} narrow={narrow}
          logo={lookAndFeel.logoDataUri} job={cloneJob} metaOf={metaOf} loadFull={loadFull}
          fieldList={fieldList} attention={cfg.cloneAttention} loadProjects={loadProjects} loadParents={loadParents} onGrid={onGrid} typeIn={typeIn}
          onStart={startClone} onClose={() => setCloneOpen(false)} onFilterClones={filterClones} />
      ) : null}
      {rsReportOpen && rsJob ? (
        <RescheduleReport ui={ui} S={S} job={rsJob} onClose={() => setRsReportOpen(false)}
          onOpenKey={(k) => { const it = allItems.get(k); if (it) setPopupItem(it); }} />
      ) : null}
    </>
  );

  // ONE WORK ITEM, AND NOTHING ELSE. Before any of the page's own chrome:
  // this tab was opened by a middle click on a row, and what was asked for
  // was that work item's screen.
  if (soloKey) {
    return (
      <WorkItemPage issueKey={soloKey} ui={ui}
        backHref={typeof window !== 'undefined' ? window.location.pathname : null}
        backLabel={pageName} />
    );
  }

  // Full screen is a sheet over the tab's pane, not a class on a scroller —
  // whatever the page draws above the structure would otherwise keep its
  // height and the structure would still be read through a letterbox. It is
  // portalled into the pane (absolute, under the top nav's z-index so its
  // menus still open over it); outside a tab it falls back to the window.
  if (full) {
    const sheet = (
      <div style={{ position: sheetHost ? 'absolute' : 'fixed', inset: 0, zIndex: sheetHost ? 240 : 1800, background: ui.page, color: ui.fg, fontFamily: FONT, overflow: 'auto', padding: '8px 10px' }}>
        {body}
        <WorkItemPopup issue={popupItem} onClose={() => setPopupItem(null)} ui={ui} narrow={narrow}
          title={(popupItem && popupItem.fields && popupItem.fields.summary) || ''}
          onOpenTab={(k) => openTab(`/browse/${k}`, k)}
          onChanged={onPopupChanged} />
      </div>
    );
    return sheetHost ? createPortal(sheet, sheetHost) : sheet;
  }

  return (
    <div ref={rootRef} style={S.scroll}>
      <div style={S.wrap}>
        {body}
      </div>

      <WorkItemPopup issue={popupItem} onClose={() => setPopupItem(null)} ui={ui} narrow={narrow}
        title={(popupItem && popupItem.fields && popupItem.fields.summary) || ''}
        onOpenTab={(k) => openTab(`/browse/${k}`, k)}
        onChanged={onPopupChanged} />
      {/* The field list the rule editor and the column menus type against. */}
      <datalist id="wip-fields">{(fieldList || []).map((f2) => <option key={f2.id || f2.key} value={f2.name} />)}</datalist>
      {adminMode && editRule ? (
        <RuleEditor edit={editRule} draft={draft} setDraft={setDraft} ui={ui} S={S}
          testZql={testZql} fieldList={fieldList} onClose={() => setEditRule(null)} workTypes={workTypes}
          sample={allItems.size ? allItems.values().next().value : null}
          headingsOf={(name, types) => tableHeadings(allItems, fieldIdOf(name), types)}
          previewGroup={(g, folderId) => { const r = groupValueOf(allItems.values().next().value, g, folderId); const one = (x) => (x && typeof x === 'object' ? x.label : String(x)); return Array.isArray(r) ? r.map(one).join(' + ') : one(r); }} />
      ) : null}
    </div>
  );
}
